/**
 * Main Application Component
 * 
 * Handles the overall application structure and initialization.
 */

import { GameEngine } from '../core/GameEngine.js';
import { GridCanvas } from './GridCanvas.js';
import { ResourcePanel } from './ResourcePanel.js';
import { MilitaryPanel } from './MilitaryPanel.js';
import { KeyboardShortcuts } from './KeyboardShortcuts.js';
import { Modal, ImportExportModal } from './Modal.js';
import { CursorSelector } from './CursorSelector.js';
import { OptimalBuilding } from './OptimalBuilding.js';
import { OriginalTilePreview } from './OriginalTilePreview.js';
import { BuildingStats } from './BuildingStats.js';
import { KeyboardShortcutsDisplay } from './KeyboardShortcutsDisplay.js';
import { BuildingList } from './BuildingList.js';
import { CityInformation } from './CityInformation.js';
import { LayoutMenu } from './LayoutMenu.js';
import { MyLayoutsMenu } from './MyLayoutsMenu.js';
import { MyFavoritesMenu } from './MyFavoritesMenu.js';
import { QuadrantMap } from './QuadrantMap.js';
import { ZONE } from '../core/Constants.js';
import { AssetLoader } from '../utils/AssetLoader.js';
import { settings } from '../utils/Settings.js';
import { getImagePath } from '../utils/imagePath.js';
import { WOOD, STONE, IRON, FOOD, GOLD, ANY, CASA, MILL, DEEP_WATER, TERRAIN_TYPE, IS_LAND, OPEN, CUTTER, QUARRY, MINE, FARM, MANOR, BAZAAR, WHARF, CELLARIUM, CACHE } from '../core/Constants.js';
import { SectionDragDrop } from '../utils/SectionDragDrop.js';
import { CrossContainerDragDrop } from '../utils/CrossContainerDragDrop.js';
import { TrashManager } from '../utils/TrashManager.js';
// Game mode imports
import { GameState } from '../game/GameState.js';
import { GameLoop } from '../game/GameLoop.js';
import { GameHUD } from './GameHUD.js';
import { ConstructionQueue } from './ConstructionQueue.js';
import { NotificationSystem } from './NotificationSystem.js';
import { GameUI } from './GameUI.js';
import { WorldMapView } from './WorldMapView.js';

export class MainApp {
    constructor() {
        this.appElement = document.getElementById('app');
        this.gameEngine = null;
        this.gridCanvas = null;
        this.resourcePanel = null;
        this.militaryPanel = null;
        this.keyboardShortcuts = null;
        this.importExportModal = null;
        this.exportToGameModal = null;
        this.cursorSelector = null;
        this.optimalBuilding = null;
        this.originalTilePreview = null;
        this.buildingStats = null;
        this.keyboardShortcutsDisplay = null;
        this.buildingList = null;
        this.cityInformation = null;
        this.assetLoader = null;
        this.selectedBuildingKey = null; // Currently selected building key from shortcuts
        
        // Game mode properties
        this.gameMode = false;
        this.gameState = null;
        this.gameLoop = null;
        this.gameHUD = null;
        this.gameUI = null; // New game UI component
        this.constructionQueue = null;
        this.notificationSystem = null;
        this.gameUIContainer = null; // Container for game UI
        this.worldMapView = null; // World map view component
        this.worldMapModal = null; // World map modal container
    }
    
    /**
     * Initialize the main application
     */
    async init() {
        if (!this.appElement) {
            throw new Error('App element not found');
        }
        
        try {
            // Load saved theme preference early (before UI creation)
            this.initTheme();
            
            // Initialize asset loader and preload images
            this.assetLoader = new AssetLoader();
            await this.assetLoader.preloadAll();
            
            // Initialize game engine
            this.gameEngine = new GameEngine();
            
            // Expose debug function to window for easy console access
            window.debugFood = () => this.gameEngine.debugFoodProduction();
            
            // Load saved weights
            this.gameEngine.wood_weight = settings.get('wood_weight');
            this.gameEngine.stone_weight = settings.get('stone_weight');
            this.gameEngine.iron_weight = settings.get('iron_weight');
            this.gameEngine.food_weight = settings.get('food_weight');
            this.gameEngine.gold_weight = settings.get('gold_weight');
            
            // Create the UI structure
            this.createUI();
            
            // Update theme button text after UI is created
            this.initTheme();
            
            // Ensure logo is updated after DOM is ready (logo is in static HTML)
            setTimeout(() => this.updateLogo(), 0);
            
            // Initialize canvas (await to ensure it's ready before setupEventListeners)
            await this.initCanvas();
            
        // Initialize panels
        this.initPanels();
            
            // Initialize left panel components
            this.initLeftPanel();
            
            // Initialize status bar
            this.initStatusBar();
            this.initBuildingList();
            
            // Initialize collapsible sections
            this.initCollapsibleSections();
            
            // Initialize section drag and drop for right panel
            this.initSectionDragDrop();
            
            // Setup keyboard shortcuts
            this.keyboardShortcuts = new KeyboardShortcuts(this.gameEngine, this.gridCanvas);
            this.setupKeyboardHandlers();
            
            // Initialize quadrant map
            const quadrantMapEl = document.getElementById('quadrant-map');
            if (quadrantMapEl) {
                this.quadrantMap = new QuadrantMap(quadrantMapEl, this.gameEngine);
                
                // Wire up quadrant actions
                this.quadrantMap.onQuadrantAction = (quadrant, action) => {
                    switch (action) {
                        case 'resources':
                            this.gameEngine.clearResourcesByQuadrant(quadrant);
                            break;
                        case 'buildings':
                            this.gameEngine.clearBuildingsByQuadrant(quadrant);
                            break;
                        case 'non-lakes':
                            this.gameEngine.clearNonLakesByQuadrant(quadrant);
                            break;
                        case 'casas':
                            this.gameEngine.clearCottagesByQuadrant(quadrant);
                            break;
                        case 'lakes':
                            this.gameEngine.clearLakesByQuadrant(quadrant);
                            break;
                    }
                    this.updateUI();
                    // Update calculation after clearing (will be called by QuadrantMap, but ensure it happens)
                    if (this.quadrantMap) {
                        setTimeout(() => {
                            this.quadrantMap.updateCalculationAfterClear();
                        }, 150);
                    }
                };
                
                // Wire up map actions
                this.quadrantMap.onMapAction = (action) => {
                    switch (action) {
                        case 'resources':
                            this.gameEngine.clearResources();
                            break;
                        case 'buildings':
                            this.gameEngine.clearBuildings();
                            break;
                        case 'non-lakes':
                            this.gameEngine.clearNonLakes();
                            break;
                        case 'casas':
                            this.gameEngine.clearCottages();
                            break;
                        case 'lakes':
                            this.gameEngine.clearLakes();
                            break;
                    }
                    this.updateUI();
                    // Update calculation after clearing
                    if (this.quadrantMap) {
                        setTimeout(() => {
                            this.quadrantMap.updateCalculationAfterClear();
                        }, 150);
                    }
                };
            }
            
            // Initialize layout menu
            this.layoutMenu = new LayoutMenu();
            this.layoutMenu.onLoadLayout = (layout) => {
                // Handle both old format (just string) and new format (object)
                const layoutString = typeof layout === 'string' ? layout : layout.layoutString;
                const layoutTroops = typeof layout === 'object' && layout.layoutTroops ? layout.layoutTroops : null;
                
                // Check if this is from the Overlays category (uses loadFromLink instead of loadFromShareString)
                const isOverlay = typeof layout === 'object' && 
                    Object.keys(this.layoutMenu.layouts).some(cat => 
                        cat === 'Overlays' && this.layoutMenu.layouts[cat].some(l => l === layout)
                    );
                
                if (confirm('Load this layout? This will replace your current city layout.')) {
                    this.gameEngine.undoRedo.saveState();
                    if (isOverlay) {
                        // Overlays use loadFromLink format
                        this.gameEngine.loadFromLink(layoutString, true, true);
                    } else {
                        // Regular layouts use loadFromShareString
                        this.gameEngine.loadFromShareString(layoutString, true, null);
                    }
                    this.gameEngine.updateTotals();
                    
                    // Load troop numbers if available
                    if (layoutTroops && Array.isArray(layoutTroops) && this.militaryPanel) {
                        // Check if there are any non-zero values
                        const hasTroops = layoutTroops.some(count => count > 0);
                        if (hasTroops) {
                            this.militaryPanel.setTroopNumbersArray(layoutTroops);
                        }
                    }
                    
                    // Load layout resources if available
                    const layoutResources = typeof layout === 'object' && layout.layoutResources ? layout.layoutResources : null;
                    if (layoutResources && Array.isArray(layoutResources) && this.resourcePanel) {
                        this.resourcePanel.setLayoutResources(layoutResources);
                    }
                    
                    // Auto-populate City Information with layout metadata
                    if (this.cityInformation && typeof layout === 'object') {
                        const categoryName = Object.keys(this.layoutMenu.layouts).find(cat => 
                            this.layoutMenu.layouts[cat].some(l => l === layout)
                        ) || '';
                        
                        // Set metadata for potential saving
                        this.cityInformation.setLayoutMetadata({
                            LayoutName: layout.layoutName || '',
                            Remarks: layout.layoutRemark || '',
                            Notes: layout.layoutNote || '',
                            mapType: categoryName || '',
                            Sharestring: layoutString || ''
                        });
                    } else if (this.cityInformation) {
                        // Clear layout metadata when loading from LayoutMenu (static data, no metadata)
                        this.cityInformation.clearLayoutMetadata();
                    }
                    this.updateUI();
                }
            };
            
            // Initialize my layouts menu
            this.myLayoutsMenu = new MyLayoutsMenu();
            this.myLayoutsMenu.onLoadLayout = (layout) => {
                this.handleLoadLayoutFromMenu(layout);
            };
            
            // Initialize my favorites menu
            this.myFavoritesMenu = new MyFavoritesMenu();
            this.myFavoritesMenu.onLoadLayout = (layout) => {
                this.handleLoadLayoutFromMenu(layout);
            };
            
            // Setup event listeners
            this.setupEventListeners();
            
            // Wait for canvas to be initialized (it uses setTimeout)
            await new Promise(resolve => setTimeout(resolve, 10));
            
            // Load backgrounds
            if (this.gridCanvas) {
                await this.gridCanvas.loadBackgrounds();
                
                // Initialize wheat fields for any existing farms on the map
                if (this.gameEngine) {
                    this.gameEngine.initializeWheatFields();
                }
                
                // Initial render
                this.gridCanvas.update();
            } else {
                console.error('gridCanvas not initialized yet');
            }
            
            // Update scale after initial render to ensure canvas fits
            setTimeout(() => {
                if (this.gridCanvas && this.gridCanvas.updateScale) {
                    this.gridCanvas.updateScale();
                }
            }, 100);
            
            // Update totals and UI to populate stats strip
            this.gameEngine.updateTotals();
            this.updateUI();
            
              // Load from URL parameters if present
              this.loadFromURL();
              
              // Start message auto-clear timer (like original Flash version)
              this.startMessageAutoClear();
          } catch (error) {
              console.error('Failed to initialize:', error);
              throw error;
          }
      }
      
      /**
       * Auto-clear messages after 3 seconds if unchanged (matches original Flash behavior)
       */
      startMessageAutoClear() {
          let lastMessage = "";
          let count = 0;
          
          const clearMessage = () => {
              if (!this.gameEngine) return;
              
              if (lastMessage === this.gameEngine.message) {
                  count++;
              } else {
                  count = 0;
              }
              
              // Clear after 30 ticks (3 seconds at 100ms intervals)
              if (count >= 30 && this.gameEngine.message) {
                  this.gameEngine.message = "";
                  count = 0;
                  this.updateUI();
              }
              
              lastMessage = this.gameEngine.message;
              setTimeout(clearMessage, 100);
          };
          
          setTimeout(clearMessage, 100);
      }
    
    /**
     * Handle loading layout from menu (My Layouts or My Favorites)
     */
    handleLoadLayoutFromMenu(layout) {
        // Handle database layout format
        const layoutString = layout.layoutString || '';
        const layoutTroops = layout.layoutTroops;
        const dbLayout = layout._dbLayout; // Full database layout object
        
        if (confirm('Load this layout? This will replace your current city layout.')) {
            this.gameEngine.undoRedo.saveState();
            // Prefer LongLink over Sharestring
            const layoutToLoad = dbLayout?.LongLink || dbLayout?.Sharestring || layoutString;
            this.gameEngine.loadFromShareString(layoutToLoad, true, null);
            this.gameEngine.updateTotals();
            
            // Load troop numbers if available
            if (layoutTroops && Array.isArray(layoutTroops) && this.militaryPanel) {
                const hasTroops = layoutTroops.some(count => count > 0);
                if (hasTroops) {
                    this.militaryPanel.setTroopNumbersArray(layoutTroops);
                }
            }
            
            // Load layout resources if available
            const layoutResources = layout.layoutResources;
            if (layoutResources && Array.isArray(layoutResources) && this.resourcePanel) {
                this.resourcePanel.setLayoutResources(layoutResources);
            }
            
            // Auto-populate City Information with database layout metadata
            if (this.cityInformation && dbLayout) {
                this.cityInformation.setLayoutMetadata({
                    id: dbLayout.id,
                    LayoutName: dbLayout.LayoutName || '',
                    Creator: dbLayout.Creator || '',
                    ShortLink: dbLayout.ShortLink || '',
                    LongLink: dbLayout.LongLink || '',
                    Sharestring: dbLayout.Sharestring || '',
                    WaLa: dbLayout.WaLa || '',
                    mapType: dbLayout.mapType || '',
                    Remarks: dbLayout.Remarks || '',
                    Notes: dbLayout.Notes || '',
                    private: dbLayout.private || 0
                });
            }
            this.updateUI();
        }
    }
    
    /**
     * Create the main UI structure
     */
    createUI() {
        this.appElement.innerHTML = `
            <a href="#main" class="skip-link" style="position:absolute;left:-9999px;">Skip to content</a>
            <div class="app-shell">
                <div class="page">
                    <section class="content-area">
                        <section class="hero" aria-label="Overview">
                            <div class="hero-inner">
                                <h1 class="hero-title">City Planner by GFunky+</h1>
                                <p class="hero-sub">Plan, optimize, and conquer with modern tools for Crown of The Gods.</p>
                                <p class="hero-links" style="margin-top: 10px; font-size: 13px;">
                                    <a href="${this.getBasePath()}developer-notes.php" style="color: var(--accent-color); text-decoration: none; border-bottom: 1px solid var(--accent-color);">Developer Notes</a>
                                </p>
                            </div>
                        </section>
                        <main id="main" class="main" role="main">
                            <div class="card">
                                <div class="city-planner">
                                    <!-- Toolbar -->
                                    <header class="toolbar">
                                        <div class="toolbar-left">
                                            <button id="btn-new">New</button>
                                            <button id="btn-reset">Reset</button>
                                            <button id="btn-import-export" class="pulsate-button"><span class="rotating-text">Share</span></button>
                                            <button id="btn-optimize">Optimize</button>
                                            <button id="btn-layouts">Gfunky+ Layouts</button>
                                            <button id="btn-my-layouts">My Layouts</button>
                                            <button id="btn-my-favorites">My Favorites</button>
                                            <button id="btn-export-to-game">Export to Game</button>
                                            <button id="btn-game-mode" class="game-mode-toggle" title="Toggle Game Mode">🎮 Game Mode</button>
                                        </div>
                                        <div class="toolbar-right">
                                            <div class="level-controls" style="display: flex; align-items: center; gap: 10px; margin-right: 10px;">
                                                <label style="display: flex; align-items: center; gap: 5px;">
                                                    <span>Basilica:</span>
                                                    <select id="basilica-level" style="padding: 2px 5px;">
                                                        ${Array.from({length: 10}, (_, i) => `<option value="${i+1}" ${i+1 === 10 ? 'selected' : ''}>${i+1}</option>`).join('')}
                                                    </select>
                                                </label>
                                                <label style="display: flex; align-items: center; gap: 5px;">
                                                    <span>Buildings:</span>
                                                    <select id="building-level" style="padding: 2px 5px;">
                                                        ${Array.from({length: 10}, (_, i) => `<option value="${i+1}" ${i+1 === 10 ? 'selected' : ''}>${i+1}</option>`).join('')}
                                                    </select>
                                                </label>
                                                <label style="display: flex; align-items: center; gap: 5px;">
                                                    <span>Wall:</span>
                                                    <select id="wall-level" style="padding: 2px 5px;">
                                                        ${Array.from({length: 11}, (_, i) => `<option value="${i}" ${i === 10 ? 'selected' : ''}>${i}</option>`).join('')}
                                                    </select>
                                                </label>
                                            </div>
                                            <label>
                                                <input type="checkbox" id="show-gridlines"> Show Gridlines
                                            </label>
                                            <label>
                                                <input type="checkbox" id="show-building-levels"> Show Building Levels
                                            </label>
                                            <div class="terrain-toggle">
                                                <button class="terrain-btn active" data-terrain="land">Land</button>
                                                <button class="terrain-btn" data-terrain="water">Water</button>
                                            </div>
                                            <button id="dragDropToggle" class="drag-drop-toggle" title="Edit Page Layout - Enable/disable drag and drop for sections" aria-label="Edit Page Layout">📐</button>
                                            <button id="trashBtn" class="trash-btn" title="Trash - Drag sections here to hide them" aria-label="Trash">🗑️</button>
                                        </div>
                                    </header>
                                    
                                    <!-- Game HUD (only visible in game mode) -->
                                    <div id="game-hud-container" class="game-hud-container" style="display: none;"></div>
                                    
                                    <!-- Game UI Container (full screen game mode) -->
                                    <div id="game-ui-container" class="game-ui-container" style="display: none;"></div>
                                    
                                    <!-- Main Container -->
                                    <div class="main-container" id="main-container-panels">
                                       <!-- Left Panel -->
                                       <aside class="left-panel" id="left-panel-sections" data-panel-id="left-panel">
                                           <div class="collapsible-section" data-section-id="cursor-size">
                                               <div class="collapsible-header">
                                                   <button class="btn-collapse collapsed" data-target="cursor-selector-section">▼</button>
                                                   <h4>Cursor Size</h4>
                                               </div>
                                               <div class="collapsible-content" id="cursor-selector-section" style="display: none;">
                                                   <div id="cursor-selector"></div>
                                               </div>
                                           </div>
                                           <div class="collapsible-section" data-section-id="tile-info">
                                               <div class="collapsible-header">
                                                   <button class="btn-collapse collapsed" data-target="tile-info-section">▼</button>
                                                   <h4>Tile Info</h4>
                                               </div>
                                               <div class="collapsible-content" id="tile-info-section" style="display: none;">
                                                   <div class="tile-info-container">
                                                       <div id="optimal-building"></div>
                                                       <div id="original-tile"></div>
                                                   </div>
                                                   <div id="building-stats"></div>
                                               </div>
                                           </div>
                                           <div class="collapsible-section" data-section-id="clear-operations">
                                               <div class="collapsible-header">
                                                   <button class="btn-collapse collapsed" data-target="quadrant-map-section">▼</button>
                                                   <h4>Clear Operations</h4>
                                               </div>
                                               <div class="collapsible-content" id="quadrant-map-section" style="display: none;">
                                                   <div id="quadrant-map"></div>
                                               </div>
                                           </div>
                                           <div class="collapsible-section" data-section-id="building-selector">
                                               <div class="collapsible-header">
                                                   <button class="btn-collapse" data-target="keyboard-shortcuts-section">▲</button>
                                                   <h4>Building Selector</h4>
                                               </div>
                                               <div class="collapsible-content" id="keyboard-shortcuts-section">
                                                   <div id="keyboard-shortcuts"></div>
                                               </div>
                                           </div>
                                       </aside>
                                        
                                        <!-- Canvas Area -->
                                        <main class="canvas-area" id="canvas-area-sections" data-panel-id="canvas-area">
                                            <!-- Info Bars at Top of Canvas Area -->
                                            <div class="canvas-info-bars">
                                                <div class="canvas-building-display" id="canvas-building-display"></div>
                                                <div class="canvas-total-display" id="canvas-total-display"></div>
                                                <div class="canvas-resource-display" id="canvas-resource-display"></div>
                                                <div class="canvas-military-display" id="canvas-military-display"></div>
                                            </div>
                                            
                                            <div class="canvas-layers">
                                                <canvas id="city-grid-bg" class="canvas-layer"></canvas>
                                                <canvas id="city-grid-lines" class="canvas-layer"></canvas>
                                                <canvas id="city-grid-overlay" class="canvas-layer"></canvas>
                                            </div>
                                            <div id="cursor-overlay"></div>
                                            <!-- Canvas Notification -->
                                            <div id="canvas-notification" class="canvas-notification"></div>
                                            
                                            <!-- Resource and Military Tabs (moved under canvas) -->
                                            <div class="tabs-below-canvas">
                                                <div class="collapsible-section" data-section-id="resources-military">
                                                    <div class="collapsible-header">
                                                        <button class="btn-collapse" data-target="resources-military-section">▲</button>
                                                        <h4>Resources & Military</h4>
                                                    </div>
                                                    <div class="collapsible-content" id="resources-military-section">
                                                        <div class="tab-container">
                                                            <button class="tab-btn active" data-tab="resources">Resources</button>
                                                            <button class="tab-btn" data-tab="military">Military</button>
                                                        </div>
                                                        <div id="resources-panel" class="tab-content active"></div>
                                                        <div id="military-panel" class="tab-content"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </main>
                                        
                                        <!-- Right Panel (Status Bar Content) -->
                                        <aside class="right-panel" id="right-panel-sections" data-panel-id="right-panel">
                                            <div class="collapsible-section" data-section-id="load-layout">
                                                <div class="collapsible-header">
                                                    <button class="btn-collapse" data-target="load-layout-section">▲</button>
                                                    <h4>Load Layout</h4>
                                                </div>
                                                <div class="collapsible-content" id="load-layout-section">
                                                    <div style="display: flex; flex-direction: column; gap: 8px;">
                                                        <input 
                                                            type="text" 
                                                            id="load-layout-input" 
                                                            placeholder="Paste link or code (ShortCode, ShortLink, LongLink, Sharestring)"
                                                            style="width: 100%; padding: 8px; border: 1px solid var(--border-color); border-radius: 4px; background-color: var(--bg-color); color: var(--text-color); font-size: 12px;"
                                                        />
                                                        <button 
                                                            id="load-layout-btn" 
                                                            style="width: 100%; padding: 8px; background-color: var(--accent-color); color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; font-weight: 600;"
                                                        >
                                                            Load Layout
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="collapsible-section" data-section-id="city-information">
                                                <div class="collapsible-header">
                                                    <button class="btn-collapse" data-target="city-information-section">▲</button>
                                                    <h4>City Information</h4>
                                                </div>
                                                <div class="collapsible-content" id="city-information-section">
                                                    <div id="city-information"></div>
                                                </div>
                                            </div>
                                            <div class="collapsible-section" data-section-id="building-list">
                                                <div class="collapsible-header">
                                                    <button class="btn-collapse" data-target="building-list-section">▲</button>
                                                    <h4>Building List</h4>
                                                </div>
                                                <div class="collapsible-content" id="building-list-section">
                                                    <div id="building-list"></div>
                                                </div>
                                            </div>
                                            <div class="collapsible-section" data-section-id="construction-queue" style="display: none;" id="construction-queue-section">
                                                <div class="collapsible-header">
                                                    <button class="btn-collapse" data-target="construction-queue-content">▲</button>
                                                    <h4>Construction Queue</h4>
                                                </div>
                                                <div class="collapsible-content" id="construction-queue-content">
                                                    <div id="construction-queue"></div>
                                                </div>
                                            </div>
                                        </aside>
                                    </div>
                                </div>
                            </div>
                        </main>
                    </section>
                </div>
            </div>
        `;
    }
    
    /**
     * Initialize the canvas for grid rendering
     */
    async initCanvas() {
        // Wait a tick to ensure DOM is ready
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                try {
                    const canvasContainer = document.querySelector('.canvas-layers');
                    if (!canvasContainer) {
                        console.error('Canvas layers container not found. Available elements:', {
                            canvasArea: !!document.querySelector('.canvas-area'),
                            allDivs: document.querySelectorAll('div').length
                        });
                        reject(new Error('Canvas layers container not found'));
                        return;
                    }
                    
                    console.log('Found canvas container:', canvasContainer);
                    
                    // Create GridCanvas instance with multi-layer support
                    this.gridCanvas = new GridCanvas(canvasContainer, this.gameEngine);
                    
                    // Set initial state from settings
                    this.gridCanvas.setShowGridlines(settings.get('showGridlines'));
                    this.gridCanvas.setShowBuildingLevels(settings.get('showBuildingLevels'));
                    
                    // Set asset loader
                    this.gridCanvas.setAssetLoader(this.assetLoader);
                    
                    // Setup canvas callbacks
                    this.gridCanvas.onTileClick = (i, j, event) => {
                        this.handleTileClick(i, j, event);
                    };
                    
                    this.gridCanvas.onTileHover = (i, j) => {
                        this.handleTileHover(i, j);
                    };
                    
                    console.log('Canvas initialized successfully');
                    resolve();
                } catch (error) {
                    reject(error);
                }
            }, 0);
        });
    }
    
    /**
     * Initialize resource and military panels
     */
    initPanels() {
        // Resource Panel
        const resourcePanelEl = document.getElementById('resources-panel');
        if (resourcePanelEl) {
            this.resourcePanel = new ResourcePanel(resourcePanelEl, this.gameEngine);
            this.resourcePanel.onAddBuilding = (resource) => {
                this.handleAddBuilding(resource);
            };
            this.resourcePanel.onRemoveBuilding = (resource) => {
                this.handleRemoveBuilding(resource);
            };
            this.resourcePanel.onAddBuildings = (count) => {
                this.handleAddBuildings(count);
            };
            this.resourcePanel.onRemoveBuildings = (count) => {
                this.handleRemoveBuildings(count);
            };
            this.resourcePanel.onAddCasas = (count, useOptimize) => {
                this.handleAddCasas(count, useOptimize);
            };
            this.resourcePanel.onRemoveCasas = (count) => {
                this.handleRemoveCasas(count);
            };
            this.resourcePanel.onLockAllBuildings = () => {
                this.handleLockAllBuildings();
            };
            this.resourcePanel.onLockAllResources = () => {
                this.handleLockAllResources();
            };
            this.resourcePanel.onUnlockAll = () => {
                this.handleUnlockAll();
            };
            this.resourcePanel.onUndo = () => {
                this.handleUndo();
            };
            this.resourcePanel.onRedo = () => {
                this.handleRedo();
            };
            this.resourcePanel.onResetMap = () => {
                this.handleResetMap();
            };
        }
        
        // Military Panel
        const militaryPanelEl = document.getElementById('military-panel');
        if (militaryPanelEl) {
            this.militaryPanel = new MilitaryPanel(militaryPanelEl, this.gameEngine);
            
            // Refresh drag and drop after MilitaryPanel renders
            setTimeout(() => {
                if (this.crossContainerDragDrop) {
                    this.crossContainerDragDrop.refreshDragAndDrop();
                }
            }, 100);
        }
        
        // Refresh drag and drop after ResourcePanel renders (if it exists)
        setTimeout(() => {
            if (this.crossContainerDragDrop) {
                this.crossContainerDragDrop.refreshDragAndDrop();
            }
        }, 200);
        
        // Import/Export Modal
        this.importExportModal = new ImportExportModal();
        this.importExportModal.onImport = (text, terrain) => {
            this.handleImport(text, terrain);
        };
        this.importExportModal.onExport = (format) => {
            return this.handleExport(format);
        };
    }
    
    /**
     * Initialize left panel components
     */
    initLeftPanel() {
        // Cursor Selector
        const cursorSelectorEl = document.getElementById('cursor-selector');
        if (cursorSelectorEl) {
            this.cursorSelector = new CursorSelector(cursorSelectorEl, this.gridCanvas);
        }
        
        // Optimal Building
        const optimalBuildingEl = document.getElementById('optimal-building');
        if (optimalBuildingEl) {
            this.optimalBuilding = new OptimalBuilding(optimalBuildingEl, this.gameEngine, this.gridCanvas);
            // Store the original placeSuggested method
            const originalPlaceSuggested = this.optimalBuilding.placeSuggested.bind(this.optimalBuilding);
            // Override to add UI update after placement
            this.optimalBuilding.placeSuggested = () => {
                if (this.optimalBuilding.currentTile && this.optimalBuilding.optimalTile) {
                    originalPlaceSuggested();
                    
                    // Hide calculation when building is placed
                    if (this.quadrantMap) {
                        this.quadrantMap.hideCalculation();
                    }
                    
                    this.updateUI();
                }
            };
        }
        
        // Original Tile Preview
        const originalTileEl = document.getElementById('original-tile');
        if (originalTileEl) {
            this.originalTilePreview = new OriginalTilePreview(originalTileEl, this.gameEngine);
        }
        
        // Building Stats
        const buildingStatsEl = document.getElementById('building-stats');
        if (buildingStatsEl) {
            this.buildingStats = new BuildingStats(buildingStatsEl, this.gameEngine);
        }
        
        // Keyboard Shortcuts Display
        const shortcutsEl = document.getElementById('keyboard-shortcuts');
        if (shortcutsEl) {
            this.keyboardShortcutsDisplay = new KeyboardShortcutsDisplay(shortcutsEl, this.gameEngine);
            // Setup selection callback
            this.keyboardShortcutsDisplay.onSelectionChange = (key) => {
                this.selectedBuildingKey = key; // null if deselected or disabled
            };
        }
    }
    
    /**
     * Initialize status message (status bar removed, using canvas notification instead)
     */
    initStatusBar() {
        // Status bar removed - using canvas notification instead
        // Initialize status message
        this.updateStatusMessage();
    }
    
    /**
     * Initialize section drag and drop
     */
    initSectionDragDrop() {
        // Determine API base path
        let apiBase = '/api/user/';
        const scriptPath = window.location.pathname;
        if (scriptPath.includes('/Louopt/')) {
            apiBase = '/Louopt/api/user/';
        } else if (scriptPath.includes('/cotgopt/')) {
            apiBase = '/cotgopt/api/user/';
        } else if (scriptPath.includes('/cotg/')) {
            apiBase = '/cotg/api/user/';
        }
        
        // Initialize cross-container drag and drop (allows moving sections between panels)
        setTimeout(() => {
            this.crossContainerDragDrop = new CrossContainerDragDrop(apiBase);
        }, 50);
        
        // Initialize trash manager
        setTimeout(() => {
            this.trashManager = new TrashManager(apiBase);
            // Make trash manager accessible globally for restore functionality
            window.mainApp = this;
            
            // Setup trash button click handler
            const trashBtn = document.getElementById('trashBtn');
            if (trashBtn) {
                trashBtn.addEventListener('click', () => {
                    this.trashManager.toggleModal();
                });
            }
        }, 150);
        
        // Initialize drag and drop for panels themselves (left, canvas, right)
        // Note: SectionDragDrop will check settings and only enable if dragDropEnabled is true
        setTimeout(() => {
            this.panelDragDrop = new SectionDragDrop(
                '#main-container-panels',
                '[data-panel-id]',
                apiBase,
                true // isPanelLevel = true
            );
        }, 100);
        
        // Initialize drag and drop for resource panel sections (inside resources-panel)
        // Note: SectionDragDrop will check settings and only enable if dragDropEnabled is true
        setTimeout(() => {
            const resourcesPanel = document.getElementById('resources-panel');
            if (resourcesPanel) {
                this.resourcePanelDragDrop = new SectionDragDrop(
                    '#resources-panel .resource-panel',
                    '.optimization-section',
                    apiBase
                );
            }
        }, 200);
    }
    
    /**
     * Update status message as canvas notification
     */
    updateStatusMessage() {
        const notification = document.getElementById('canvas-notification');
        if (!notification || !this.gameEngine) return;
        
        const message = this.gameEngine.message || '';
        
        if (message) {
            notification.textContent = message;
            notification.classList.add('show');
            
            // Auto-hide after 5 seconds
            clearTimeout(this.notificationTimeout);
            this.notificationTimeout = setTimeout(() => {
                notification.classList.remove('show');
            }, 5000);
        } else {
            notification.classList.remove('show');
        }
    }
    
    /**
     * Initialize building list
     */
    initBuildingList() {
        const buildingListEl = document.getElementById('building-list');
        if (buildingListEl) {
            this.buildingList = new BuildingList(buildingListEl, this.gameEngine, this.assetLoader);
        }
        
        // Initialize city information component
        const cityInfoEl = document.getElementById('city-information');
        if (cityInfoEl) {
            this.cityInformation = new CityInformation(cityInfoEl, {
                getShareString: () => this.handleExport('sharestring'),
                getLongLink: () => this.handleExport('sharelink'),
                getTroopNumbers: () => {
                    // Get troop numbers from military panel if available
                    if (this.militaryPanel && typeof this.militaryPanel.getTroopNumbersArray === 'function') {
                        return this.militaryPanel.getTroopNumbersArray();
                    }
                    return null;
                },
                getLayoutResources: () => {
                    // Get layout resources from resource panel if available
                    if (this.resourcePanel && typeof this.resourcePanel.getLayoutResourcesArray === 'function') {
                        return this.resourcePanel.getLayoutResourcesArray();
                    }
                    return null;
                },
                setWaterMode: (isWater) => {
                    // Update game engine terrain mode when WaLa is changed
                    if (this.gameEngine) {
                        this.gameEngine.updateWaterTiles(isWater);
                        // Update canvas if available
                        if (this.gridCanvas) {
                            this.gridCanvas.setWater(isWater);
                            this.gridCanvas.update();
                        }
                        // Update UI to reflect changes
                        this.updateUI();
                    }
                }
            });
        }
    }
    
    /**
     * Initialize collapsible sections
     */
    initCollapsibleSections() {
        const collapseButtons = document.querySelectorAll('.btn-collapse');
        const toggleSection = (btn) => {
            const targetId = btn.getAttribute('data-target');
            const content = document.getElementById(targetId);
            if (content) {
                const isCollapsed = content.style.display === 'none';
                content.style.display = isCollapsed ? 'block' : 'none';
                btn.textContent = isCollapsed ? '▲' : '▼';
                btn.classList.toggle('collapsed', !isCollapsed);
            }
        };
        
        collapseButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                toggleSection(btn);
            });
            
            // Make the header clickable too
            const header = btn.closest('.collapsible-header');
            if (header) {
                header.addEventListener('click', (e) => {
                    if (e.target !== btn) {
                        toggleSection(btn);
                    }
                });
            }
        });
    }
    
    /**
     * Update logo based on current theme
     */
    updateLogo() {
        const isLight = document.documentElement.getAttribute('data-theme') === 'light';
        const logos = document.querySelectorAll('.logo, img[src*="cotgopt"]');
        
        logos.forEach(img => {
            const src = img.getAttribute('src') || img.src;
            if (isLight) {
                // Light mode: use cotgoptdark.png
                if (src && src.includes('cotgopt1.png')) {
                    img.src = src.replace('cotgopt1.png', 'cotgoptdark.png');
                }
            } else {
                // Dark mode: use cotgopt1.png
                if (src && src.includes('cotgoptdark.png')) {
                    img.src = src.replace('cotgoptdark.png', 'cotgopt1.png');
                }
            }
        });
    }
    
    /**
     * Initialize theme from localStorage
     */
    initTheme() {
        try {
            // If global theme manager exists, it will handle initialization
            // Otherwise, use local implementation
            if (typeof window.ThemeManager !== 'undefined' && window.ThemeManager.initTheme) {
                window.ThemeManager.initTheme();
            } else {
                const stored = localStorage.getItem('cotgopt-theme');
                
                // Apply theme to document element (for CSS variables)
                if (stored === 'light') {
                    document.documentElement.setAttribute('data-theme', 'light');
                } else {
                    document.documentElement.removeAttribute('data-theme');
                }
                
            // Theme toggle is now handled by global theme-toggle.js
            }
            
            // Update logo after theme is set
            this.updateLogo();
        } catch (e) {
            console.warn('Failed to load theme preference:', e);
        }
    }
    
    /**
     * Toggle between light and dark theme
     */
    toggleTheme() {
        // Use global toggleTheme if available
        if (typeof window.toggleTheme === 'function') {
            window.toggleTheme();
            // Update logo after theme change
            this.updateLogo();
        } else {
            // Fallback to local implementation if global is not available
            const isLight = document.documentElement.getAttribute('data-theme') === 'light';
            
            if (isLight) {
                // Switch to dark
                document.documentElement.removeAttribute('data-theme');
                try {
                    localStorage.setItem('cotgopt-theme', 'dark');
                } catch (e) {
                    console.warn('Failed to save theme preference:', e);
                }
            } else {
                // Switch to light
                document.documentElement.setAttribute('data-theme', 'light');
                try {
                    localStorage.setItem('cotgopt-theme', 'light');
                } catch (e) {
                    console.warn('Failed to save theme preference:', e);
                }
            }
            
            // Update logo after theme change
            this.updateLogo();
        }
    }
    
    /**
     * Setup event listeners for UI interactions
     */
    setupEventListeners() {
        // Toolbar buttons
        document.getElementById('btn-new')?.addEventListener('click', () => {
            this.handleNewCity();
        });
        
        document.getElementById('btn-reset')?.addEventListener('click', () => {
            this.handleResetCity();
        });
        
        document.getElementById('btn-import-export')?.addEventListener('click', () => {
            this.handleImportExport();
        });
        
        // Rotating text for Share button
        this.initRotatingShareButton();
        
        document.getElementById('btn-optimize')?.addEventListener('click', () => {
            this.showOptimizeOptions();
        });
        
        
        // Layout menu button
        document.getElementById('btn-layouts')?.addEventListener('click', (e) => {
            const rect = e.target.getBoundingClientRect();
            if (this.layoutMenu) {
                this.layoutMenu.toggle(rect.left, rect.bottom);
            }
        });
        
        
        // My Layouts menu button
        document.getElementById('btn-my-layouts')?.addEventListener('click', (e) => {
            const rect = e.target.getBoundingClientRect();
            if (this.myLayoutsMenu) {
                this.myLayoutsMenu.toggle(rect.left, rect.bottom);
            }
        });
        
        // My Favorites menu button
        document.getElementById('btn-my-favorites')?.addEventListener('click', (e) => {
            const rect = e.target.getBoundingClientRect();
            if (this.myFavoritesMenu) {
                this.myFavoritesMenu.toggle(rect.left, rect.bottom);
            }
        });
        
        // Game Mode toggle button
        document.getElementById('btn-game-mode')?.addEventListener('click', () => {
            this.toggleGameMode();
        });
        
        // Export to Game button
        document.getElementById('btn-export-to-game')?.addEventListener('click', () => {
            this.handleExportToGame();
        });
        
        // Gridlines toggle
        const gridlinesCheckbox = document.getElementById('show-gridlines');
        if (gridlinesCheckbox) {
            // Load saved preference
            gridlinesCheckbox.checked = settings.get('showGridlines');
            
            gridlinesCheckbox.addEventListener('change', (e) => {
                this.gridCanvas.setShowGridlines(e.target.checked);
                settings.set('showGridlines', e.target.checked);
            });
        }
        
        // Building levels toggle
        const buildingLevelsCheckbox = document.getElementById('show-building-levels');
        if (buildingLevelsCheckbox) {
            // Load saved preference
            buildingLevelsCheckbox.checked = settings.get('showBuildingLevels');
            
            buildingLevelsCheckbox.addEventListener('change', (e) => {
                this.gridCanvas.setShowBuildingLevels(e.target.checked);
                settings.set('showBuildingLevels', e.target.checked);
            });
        }
        
        
        // Terrain toggle
        document.querySelectorAll('.terrain-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const terrain = e.target.dataset.terrain;
                this.handleTerrainChange(terrain);
            });
        });
        
        // Load saved terrain preference
        const savedWater = settings.get('water');
        if (savedWater) {
            this.handleTerrainChange('water');
        } else {
            this.handleTerrainChange('land');
        }
        
        // Drag and drop toggle button (set up after drag-drop instances are initialized)
        setTimeout(() => {
            const dragDropToggleBtn = document.getElementById('dragDropToggle');
            if (dragDropToggleBtn && this.crossContainerDragDrop) {
                // Update button appearance based on initial state
                this.updateDragDropToggleButton(dragDropToggleBtn);
                
                dragDropToggleBtn.addEventListener('click', () => {
                    const enabled = this.crossContainerDragDrop.toggle();
                    
                    // Also toggle panel drag-drop
                    if (this.panelDragDrop) {
                        if (enabled) {
                            this.panelDragDrop.enable();
                        } else {
                            this.panelDragDrop.disable();
                        }
                    }
                    
                    // Also toggle resource panel drag-drop
                    if (this.resourcePanelDragDrop) {
                        if (enabled) {
                            this.resourcePanelDragDrop.enable();
                        } else {
                            this.resourcePanelDragDrop.disable();
                        }
                    }
                    
                    this.updateDragDropToggleButton(dragDropToggleBtn);
                });
            }
        }, 300);
        
        // Building level selectors
        const basilicaLevelSelect = document.getElementById('basilica-level');
        if (basilicaLevelSelect) {
            basilicaLevelSelect.value = this.gameEngine.basilicaLevel || 10;
            basilicaLevelSelect.addEventListener('change', (e) => {
                this.gameEngine.basilicaLevel = parseInt(e.target.value);
                this.gameEngine.updateTotals();
                this.updateUI();
            });
        }
        
        const buildingLevelSelect = document.getElementById('building-level');
        if (buildingLevelSelect) {
            buildingLevelSelect.value = this.gameEngine.buildingLevel || 10;
            buildingLevelSelect.addEventListener('change', (e) => {
                this.gameEngine.buildingLevel = parseInt(e.target.value);
                this.gameEngine.updateTotals();
                this.updateUI();
            });
        }
        
        const wallLevelSelect = document.getElementById('wall-level');
        if (wallLevelSelect) {
            wallLevelSelect.value = this.gameEngine.wallLevel !== undefined && this.gameEngine.wallLevel !== null ? this.gameEngine.wallLevel : 10;
            wallLevelSelect.addEventListener('change', (e) => {
                this.gameEngine.wallLevel = parseInt(e.target.value);
                this.gameEngine.updateTotals();
                this.updateUI();
            });
        }
        
        // Tab switching
        document.querySelectorAll('.tab-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const tab = e.target.dataset.tab;
                this.handleTabSwitch(tab);
            });
        });
        
        // Theme toggle is now handled by global theme-toggle.js in header/footer
        
        // Load Layout button
        const loadLayoutBtn = document.getElementById('load-layout-btn');
        const loadLayoutInput = document.getElementById('load-layout-input');
        if (loadLayoutBtn && loadLayoutInput) {
            loadLayoutBtn.addEventListener('click', () => {
                this.handleLoadLayout(loadLayoutInput.value);
            });
            
            // Allow Enter key to trigger load
            loadLayoutInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.handleLoadLayout(loadLayoutInput.value);
                }
            });
        }
    }
    
    /**
     * Setup keyboard shortcut handlers
     */
    setupKeyboardHandlers() {
        if (!this.keyboardShortcuts) return;
        
        this.keyboardShortcuts.onBuildingPlace = (keyChar, i, j) => {
            this.gameEngine.placeByChar(keyChar, i, j);
            
            // Hide calculation when building is placed
            if (this.quadrantMap) {
                this.quadrantMap.hideCalculation();
            }
            
            this.gameEngine.updateTotals();
            this.updateUI();
        };
        
        this.keyboardShortcuts.onToggleLock = (i, j) => {
            this.gameEngine.toggleLocked(i, j);
            this.gridCanvas.update();
        };
        
        this.keyboardShortcuts.onDelete = (i, j) => {
            this.gameEngine.placeByChar('`', i, j);
            this.updateUI();
        };
        
        this.keyboardShortcuts.onPlaceSuggested = () => {
            if (this.optimalBuilding) {
                this.optimalBuilding.placeSuggested();
                this.updateUI();
            }
        };
        
        this.keyboardShortcuts.onShowHeatMap = (show) => {
            if (this.gridCanvas) {
                this.gridCanvas.setShowHeatMap(show);
            }
        };
        
        // Undo/Redo handlers
        document.addEventListener('keydown', (e) => {
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z' && !e.shiftKey) {
                    e.preventDefault();
                    if (this.gameEngine.undo()) {
                        this.updateUI();
                    }
                } else if ((e.key === 'y') || (e.key === 'z' && e.shiftKey)) {
                    e.preventDefault();
                    if (this.gameEngine.redo()) {
                        this.updateUI();
                    }
                }
            }
        });
    }
    
    /**
     * Handle add multiple casas
     * @param {number} count - Number of casas to add
     * @param {boolean} useOptimize - If true, use optimization (can replace buildings). If false, only place on OPEN spots.
     */
    async handleAddCasas(count, useOptimize = false) {
        if (!this.gameEngine) {
            console.warn('Cannot add casas: gameEngine is not initialized');
            return;
        }
        
        // Validate count
        if (count <= 0 || count > 1000) {
            this.gameEngine.message = `Invalid count: ${count}. Must be between 1 and 1000.`;
            this.updateUI();
            return;
        }
        
        // Check available building slots (casas are buildings too)
        this.gameEngine.updateTotals(); // Ensure totals are up to date
        const availableSlots = (this.gameEngine.maxBuildings || 100) - this.gameEngine.building_tot;
        
        if (availableSlots <= 0) {
            this.gameEngine.message = `Cannot add casas: All ${this.gameEngine.maxBuildings || 100} building slots are full. Upgrade your Basilica to increase the limit.`;
            this.updateUI();
            return;
        }
        
        // Limit count to available slots
        if (count > availableSlots) {
            this.gameEngine.message = `Only ${availableSlots} building slot${availableSlots !== 1 ? 's' : ''} available. Limiting to ${availableSlots} casas.`;
            this.updateUI();
            count = availableSlots;
        }
        
        // Check if undoRedo is available
        if (!this.gameEngine.undoRedo) {
            console.warn('Cannot add casas: undoRedo is not initialized');
            this.gameEngine.message = 'Error: Undo/Redo system not available';
            this.updateUI();
            return;
        }
        
        try {
            this.gameEngine.undoRedo.saveState();
        } catch (err) {
            console.error('Error saving state before adding casas:', err);
            this.gameEngine.message = 'Error: Could not save state';
            this.updateUI();
            return;
        }
        
        if (useOptimize) {
            // Use optimization: find best spots, can replace buildings
            if (count > 10) {
                this.gameEngine.message = `Optimizing ${count} casa placements...`;
                this.updateUI();
            }
            
            let added = 0;
            for (let i = 0; i < count; i++) {
                // Check available slots before each placement
                this.gameEngine.updateTotals();
                const currentAvailable = (this.gameEngine.maxBuildings || 100) - this.gameEngine.building_tot;
                if (currentAvailable <= 0) {
                    this.gameEngine.message = `Stopped: All building slots are now full. Placed ${added} of ${count} casas.`;
                    this.updateUI();
                    break;
                }
                
                // Use the optimization method which can replace buildings
                const result = this.gameEngine.add(CASA);
                if (result > 0) {
                    added++;
                }
                
                // Update progress for large operations
                if (count > 10 && i % 10 === 0 && i > 0) {
                    this.gameEngine.message = `Optimizing casa placements... ${i}/${count} placed`;
                    this.updateUI();
                    // Yield to browser to prevent UI freezing
                    await new Promise(resolve => setTimeout(resolve, 0));
                }
            }
            
            // Update message
            if (added < count) {
                this.gameEngine.message = `Placed ${added} of ${count} casas using optimization.`;
            } else {
                this.gameEngine.message = `Placed ${added} casas using optimization.`;
            }
        } else {
            // Non-optimize mode: only place on OPEN spots, don't replace buildings
            if (count > 10) {
                this.gameEngine.message = `Placing ${count} casas in first available OPEN spots...`;
                this.updateUI();
            }
            
            // Get the key character for CASA
            const keyChar = this.gameEngine.tileToChar(CASA);
            if (!keyChar || keyChar === ' ') {
                this.gameEngine.message = 'Error: Could not find key binding for casa';
                this.updateUI();
                return;
            }
            
            // Get base map for terrain checking
            const baseMap = this.gameEngine.water ? this.gameEngine.waterMap : this.gameEngine.landMap;
            const TERRAIN_TYPE = 0x0300;
            const IS_LAND = 0x0200;
            
            // Find all OPEN positions first (top-left to bottom-right scan)
            const openPositions = [];
            for (let i = 0; i < 21; i++) {
                for (let j = 0; j < 21; j++) {
                    // Skip walls, town hall, deep water, and locked tiles
                    if (this.gameEngine.map[i][j] <= DEEP_WATER) continue;
                    if (this.gameEngine.isLocked(i, j)) continue;
                    if (baseMap[i][j] === DEEP_WATER) continue;
                    // Check if it's land (casas can only go on land)
                    if ((baseMap[i][j] & TERRAIN_TYPE) !== IS_LAND) continue;
                    // Only place on OPEN spots (not on existing buildings)
                    if (this.gameEngine.map[i][j] !== OPEN) continue;
                    // Found an OPEN position
                    openPositions.push({ i, j });
                }
            }
            
            // Place casas in first available OPEN positions
            let added = 0;
            const positionsToUse = openPositions.slice(0, count);
            
            for (let idx = 0; idx < positionsToUse.length && added < count; idx++) {
                // Check available slots before each placement
                this.gameEngine.updateTotals();
                const currentAvailable = (this.gameEngine.maxBuildings || 100) - this.gameEngine.building_tot;
                if (currentAvailable <= 0) {
                    this.gameEngine.message = `Stopped: All building slots are now full. Placed ${added} of ${count} casas.`;
                    this.updateUI();
                    break;
                }
                
                const position = positionsToUse[idx];
                try {
                    const casasBefore = this.gameEngine.casa_tot;
                    this.gameEngine.placeByChar(keyChar, position.i, position.j, false);
                    this.gameEngine.updateTotals();
                    
                    // Verify placement succeeded
                    if (this.gameEngine.casa_tot > casasBefore) {
                        added++;
                    }
                    
                    // Update progress for large operations
                    if (count > 10 && idx % 10 === 0 && idx > 0) {
                        this.gameEngine.message = `Placing casas... ${idx}/${positionsToUse.length} placed`;
                        this.updateUI();
                        // Yield to browser to prevent UI freezing
                        await new Promise(resolve => setTimeout(resolve, 0));
                    }
                } catch (err) {
                    console.error(`Error placing casa at (${position.i}, ${position.j}):`, err);
                }
            }
            
            // Update message
            if (added < count) {
                const remaining = count - added;
                this.gameEngine.message = `Placed ${added} of ${count} casas. ${remaining} OPEN spot${remaining !== 1 ? 's' : ''} ${remaining === 1 ? 'was' : 'were'} not available.`;
            } else {
                this.gameEngine.message = `Placed ${added} casas in OPEN spots.`;
            }
        }
        
        this.updateUI();
    }
    
    /**
     * Handle remove multiple casas
     */
    async handleRemoveCasas(count) {
        if (!this.gameEngine) {
            console.warn('Cannot remove casas: gameEngine is not initialized');
            return;
        }
        
        // Validate count
        if (count <= 0 || count > 1000) {
            this.gameEngine.message = `Invalid count: ${count}. Must be between 1 and 1000.`;
            this.updateUI();
            return;
        }
        
        // Check if undoRedo is available
        if (!this.gameEngine.undoRedo) {
            console.warn('Cannot remove casas: undoRedo is not initialized');
            this.gameEngine.message = 'Error: Undo/Redo system not available';
            this.updateUI();
            return;
        }
        
        try {
            this.gameEngine.undoRedo.saveState();
        } catch (err) {
            console.error('Error saving state before removing casas:', err);
            this.gameEngine.message = 'Error: Could not save state';
            this.updateUI();
            return;
        }
        
        // Show progress message
        if (count > 10) {
            this.gameEngine.message = `Removing ${count} casas from first positions...`;
            this.updateUI();
        }
        
        // Find all casa positions first (top-left to bottom-right scan)
        const casaPositions = [];
        for (let i = 0; i < 21; i++) {
            for (let j = 0; j < 21; j++) {
                // Check if this position has a casa
                if (this.gameEngine.map[i][j] === CASA) {
                    casaPositions.push({ i, j });
                }
            }
        }
        
        // Remove casas from first available positions
        let removed = 0;
        const positionsToRemove = casaPositions.slice(0, count);
        
        for (let idx = 0; idx < positionsToRemove.length; idx++) {
            const position = positionsToRemove[idx];
            try {
                const casasBefore = this.gameEngine.casa_tot;
                // Remove casa by setting to OPEN
                this.gameEngine.map[position.i][position.j] = OPEN;
                this.gameEngine.updateTotals();
                
                // Verify removal succeeded
                if (this.gameEngine.casa_tot < casasBefore) {
                    removed++;
                }
                
                // Update progress for large operations
                if (count > 10 && idx % 10 === 0 && idx > 0) {
                    this.gameEngine.message = `Removing casas... ${idx}/${positionsToRemove.length} removed`;
                    this.updateUI();
                    // Yield to browser to prevent UI freezing
                    await new Promise(resolve => setTimeout(resolve, 0));
                }
            } catch (err) {
                console.error(`Error removing casa at (${position.i}, ${position.j}):`, err);
            }
        }
        
        // Update message
        if (removed < count) {
            this.gameEngine.message = `Removed ${removed} of ${count} casas. ${count - removed} casas were not found.`;
        } else {
            this.gameEngine.message = `Removed ${removed} casas.`;
        }
        this.updateUI();
    }
    
    /**
     * Handle lock all buildings
     */
    handleLockAllBuildings() {
        if (!this.gameEngine) return;
        this.gameEngine.undoRedo.saveState();
        this.gameEngine.lockBuildings();
        this.gridCanvas.render();
        this.gameEngine.message = "All buildings locked.";
        this.updateUI();
    }
    
    /**
     * Handle lock all resources
     */
    handleLockAllResources() {
        if (!this.gameEngine) return;
        this.gameEngine.undoRedo.saveState();
        this.gameEngine.lockResources();
        this.gridCanvas.render();
        this.gameEngine.message = "All resources locked.";
        this.updateUI();
    }
    
    /**
     * Handle unlock all
     */
    handleUnlockAll() {
        if (!this.gameEngine) return;
        this.gameEngine.undoRedo.saveState();
        this.gameEngine.resetLocks();
        this.gridCanvas.render();
        this.gameEngine.message = "All tiles unlocked.";
        this.updateUI();
    }
    
    /**
     * Handle undo
     */
    handleUndo() {
        if (!this.gameEngine) return;
        if (this.gameEngine.undoRedo.undo()) {
            this.gameEngine.updateTotals();
            this.gridCanvas.update();
            this.gameEngine.message = "Undone.";
            this.updateUI();
        } else {
            this.gameEngine.message = "Nothing to undo.";
            this.updateUI();
        }
    }
    
    /**
     * Handle redo
     */
    handleRedo() {
        if (!this.gameEngine) return;
        if (this.gameEngine.undoRedo.redo()) {
            this.gameEngine.updateTotals();
            this.gridCanvas.update();
            this.gameEngine.message = "Redone.";
            this.updateUI();
        } else {
            this.gameEngine.message = "Nothing to redo.";
            this.updateUI();
        }
    }
    
    /**
     * Handle reset map
     */
    handleResetMap() {
        if (!this.gameEngine) return;
        if (confirm('Really reset the map? This cannot be undone.')) {
            this.gameEngine.undoRedo.saveState();
            // Reset to original map
            if (this.gameEngine.last_load_string) {
                // Clear map first to remove any old buildings/nodes
                this.gameEngine.loadFromString(this.gameEngine.last_load_string, true);
            } else {
                // Reset to empty if no saved state
                this.gameEngine.map = this.gameEngine.createMapFromTemplate(this.gameEngine.landMap);
                this.gameEngine.origMap = this.gameEngine.createMapFromTemplate(this.gameEngine.landMap);
            }
            this.gameEngine.resetLocks();
            this.gameEngine.updateTotals();
            this.gridCanvas.update();
            this.gameEngine.message = "Map reset.";
            this.updateUI();
        }
    }
    
    /**
     * Handle tile click
     */
    handleTileClick(i, j, event) {
        // Left click: Place current selection
        // Right click: Delete/context menu
        if (event.button === 0) {
            // Check if we have a selected building key from shortcuts
            if (this.selectedBuildingKey && this.gameEngine.keyBindings[this.selectedBuildingKey]) {
                // Place the selected building
                const cursorMode = this.gridCanvas.cursorMode;
                const [rows, cols] = cursorMode.split('x').map(Number);
                
                for (let di = 0; di < rows; di++) {
                    for (let dj = 0; dj < cols; dj++) {
                        const ti = i + di;
                        const tj = j + dj;
                        
                        if (ti < 21 && tj < 21) {
                            this.placeBuilding(this.selectedBuildingKey, ti, tj);
                        }
                    }
                }
                
                // Hide calculation when building is placed
                if (this.quadrantMap) {
                    this.quadrantMap.hideCalculation();
                }
                
                this.gameEngine.updateTotals();
                this.updateUI();
            }
        } else if (event.button === 2) {
            event.preventDefault();
            // Right click: Delete or context menu
            // In game mode, we might want to prevent deletion or handle it differently
            if (this.gameMode) {
                // In game mode, deletion might not be allowed or might have restrictions
                // For now, allow deletion (could add confirmation or restrictions later)
            }
            this.gameEngine.placeByChar('`', i, j);
            this.gameEngine.updateTotals();
            this.updateUI();
        }
    }
    
    /**
     * Place a building - routes through ConstructionManager in game mode, direct placement in planner mode
     * @param {string} keyChar - Building key character (e.g., 'a' for CUTTER)
     * @param {number} i - X position
     * @param {number} j - Y position
     * @returns {boolean} True if placement was successful/queued
     */
    placeBuilding(keyChar, i, j) {
        // In game mode, use ConstructionManager
        if (this.gameMode && this.gameState && this.gameState.constructionManager) {
            // Get building type from key
            const buildingBinding = this.gameEngine.keyBindings[keyChar];
            if (!buildingBinding || !buildingBinding[0]) {
                return false;
            }
            
            const buildingType = buildingBinding[0];
            const buildingLevel = this.gameEngine.buildingLevel || 10;
            
            // Queue the building for construction
            const queueItem = this.gameState.constructionManager.queueBuilding(
                buildingType,
                buildingLevel,
                i,
                j
            );
            
            if (queueItem) {
                // Show notification
                if (this.notificationSystem) {
                    const buildingName = this.getBuildingNameForNotification(buildingType);
                    this.notificationSystem.info(`${buildingName} queued for construction`);
                }
                
                // Update construction queue display
                if (this.constructionQueue) {
                    this.constructionQueue.update();
                }
                
                return true;
            } else {
                // Failed to queue (insufficient resources, invalid position, etc.)
                if (this.notificationSystem) {
                    this.notificationSystem.error('Failed to queue building. Check resources and position.');
                }
                return false;
            }
        } else {
            // Planner mode - direct placement
            this.gameEngine.placeByChar(keyChar, i, j);
            return true;
        }
    }
    
    /**
     * Handle tile hover
     */
    handleTileHover(i, j) {
        // Update optimal building suggestion
        if (this.optimalBuilding) {
            this.optimalBuilding.update(i, j);
        }
        
        // Update original tile preview
        if (this.originalTilePreview) {
            this.originalTilePreview.update(i, j);
        }
        
        // Update building stats
        if (this.buildingStats) {
            this.buildingStats.update(i, j);
        }
    }
    
    /**
     * Handle add building
     */
    handleAddBuilding(resource) {
        // In game mode, we can't just add buildings - they need to be queued
        // For now, show a message that this feature needs building selection
        if (this.gameMode) {
            if (this.notificationSystem) {
                this.notificationSystem.info('Please select a building from the shortcuts and click to place it.');
            }
            return;
        }
        
        // Planner mode - use existing behavior
        // Map resource name to constant
        const resourceMap = {
            'wood': WOOD,
            'stone': STONE,
            'iron': IRON,
            'food': FOOD,
            'gold': GOLD
        };
        
        const resourceType = resourceMap[resource];
        if (resourceType !== undefined && this.gameEngine) {
            this.gameEngine.undoRedo.saveState();
            this.gameEngine.add(resourceType);
            this.updateUI();
        }
    }
    
    /**
     * Handle remove building
     */
    handleRemoveBuilding(resource) {
        // Map resource name to constant
        const resourceMap = {
            'wood': WOOD,
            'stone': STONE,
            'iron': IRON,
            'food': FOOD,
            'gold': GOLD
        };
        
        const resourceType = resourceMap[resource];
        if (resourceType !== undefined && this.gameEngine) {
            this.gameEngine.undoRedo.saveState();
            this.gameEngine.remove(resourceType);
            this.updateUI();
        }
    }
    
    /**
     * Handle add multiple buildings
     */
    async handleAddBuildings(count, resourceType = null) {
        if (!this.gameEngine) return;
        
        // Validate count
        if (count <= 0 || count > 1000) {
            this.gameEngine.message = `Invalid count: ${count}. Must be between 1 and 1000.`;
            this.updateUI();
            return;
        }
        
        // Check available building slots
        this.gameEngine.updateTotals(); // Ensure totals are up to date
        const availableSlots = (this.gameEngine.maxBuildings || 100) - this.gameEngine.building_tot;
        
        if (availableSlots <= 0) {
            this.gameEngine.message = `Cannot add buildings: All ${this.gameEngine.maxBuildings || 100} building slots are full. Upgrade your Basilica to increase the limit.`;
            this.updateUI();
            return;
        }
        
        // Limit count to available slots
        if (count > availableSlots) {
            this.gameEngine.message = `Only ${availableSlots} building slot${availableSlots !== 1 ? 's' : ''} available. Limiting to ${availableSlots} buildings.`;
            this.updateUI();
            count = availableSlots;
        }
        
        this.gameEngine.undoRedo.saveState();
        
        // Show progress message for large operations
        if (count > 10) {
            this.gameEngine.message = `Adding ${count} buildings...`;
            this.updateUI();
        }
        
        let added = 0;
        let consecutiveFailures = 0;
        const maxConsecutiveFailures = 10; // Stop if we can't place 10 in a row
        
        for (let i = 0; i < count; i++) {
            // Check available slots before each placement
            this.gameEngine.updateTotals();
            const currentAvailable = (this.gameEngine.maxBuildings || 100) - this.gameEngine.building_tot;
            if (currentAvailable <= 0) {
                this.gameEngine.message = `Stopped: All building slots are now full. Added ${added} of ${count} buildings.`;
                this.updateUI();
                break;
            }
            let result = 0;
            if (resourceType !== null) {
                result = this.gameEngine.add(resourceType);
            } else {
                // Use ANY to add optimal building using all weights
                result = this.gameEngine.add(ANY);
            }
            
            // Accept any non-zero result as a successful placement (even if negative)
            // result === 0.0 means no placement was found
            // result !== 0.0 means a placement was made (even if the value is negative)
            if (result !== 0.0) {
                added++;
                consecutiveFailures = 0; // Reset failure counter on success
            } else {
                consecutiveFailures++;
                // If we can't place buildings after several attempts, stop early
                if (consecutiveFailures >= maxConsecutiveFailures) {
                    this.gameEngine.message = `Stopped early: Couldn't find valid placements after ${consecutiveFailures} attempts. Added ${added} of ${count} buildings.`;
                    this.updateUI();
                    break;
                }
            }
            
            // Update progress for large operations
            if (count > 10 && i % 10 === 0 && i > 0) {
                this.gameEngine.message = `Adding buildings... ${i}/${count} (${added} placed)`;
                this.updateUI();
                // Yield to browser to prevent UI freezing
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }
        
        if (count > 10) {
            this.gameEngine.message = `Added ${added} of ${count} buildings.`;
        }
        
        // Fine-tune after adding multiple buildings (improves placement)
        if (count >= 10 && added > 0) {
            this.gameEngine.message = `Fine-tuning placements...`;
            this.updateUI();
            const improvements = this.gameEngine.fineTune(50);
            if (improvements > 0) {
                this.gameEngine.message = `Fine-tuned ${improvements} building placements.`;
            }
        }
        
        // Hide calculation when buildings are added
        if (this.quadrantMap) {
            this.quadrantMap.hideCalculation();
        }
        
        this.updateUI();
    }
    
    /**
     * Handle remove multiple buildings
     */
    async handleRemoveBuildings(count, resourceType = null) {
        if (!this.gameEngine) return;
        
        // Validate count
        if (count <= 0 || count > 1000) {
            this.gameEngine.message = `Invalid count: ${count}. Must be between 1 and 1000.`;
            this.updateUI();
            return;
        }
        
        this.gameEngine.undoRedo.saveState();
        
        // Show progress message for large operations
        if (count > 10) {
            this.gameEngine.message = `Removing ${count} buildings...`;
            this.updateUI();
        }
        
        let removed = 0;
        for (let i = 0; i < count; i++) {
            if (resourceType !== null) {
                const result = this.gameEngine.remove(resourceType);
                if (result > 0) removed++;
            } else {
                // Use ANY to remove worst building using all weights
                const result = this.gameEngine.remove(ANY);
                if (result > 0) removed++;
            }
            
            // Update progress for large operations
            if (count > 10 && i % 10 === 0 && i > 0) {
                this.gameEngine.message = `Removing buildings... ${i}/${count}`;
                this.updateUI();
                // Yield to browser to prevent UI freezing
                await new Promise(resolve => setTimeout(resolve, 0));
            }
        }
        
        if (count > 10) {
            this.gameEngine.message = `Removed ${removed} of ${count} buildings.`;
        }
        
        // Fine-tune after removing multiple buildings (optimizes remaining placements)
        if (count >= 10 && removed > 0) {
            this.gameEngine.message = `Fine-tuning placements...`;
            this.updateUI();
            const improvements = this.gameEngine.fineTune(50);
            if (improvements > 0) {
                this.gameEngine.message = `Fine-tuned ${improvements} building placements.`;
            }
        }
        
        this.updateUI();
    }
    
    /**
     * Show optimize options modal
     */
    showOptimizeOptions() {
        if (!this.gameEngine) return;
        
        // Create a simple modal for optimize options
        const modalId = 'optimize-options-modal';
        let modal = document.getElementById(modalId);
        
        if (!modal) {
            modal = document.createElement('div');
            modal.id = modalId;
            modal.className = 'modal';
            modal.innerHTML = `
                <div class="modal-overlay"></div>
                <div class="modal-content" style="max-width: 500px;">
                    <div class="modal-header">
                        <h2 class="modal-title">Optimize City</h2>
                        <button class="modal-close" aria-label="Close">&times;</button>
                    </div>
                    <div class="modal-body" style="padding: 20px;">
                        <p style="margin-bottom: 12px;">Choose optimization area:</p>
                        <div style="margin-bottom: 20px;">
                            <label style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                                <input type="radio" name="optimize-area" value="map" checked style="cursor: pointer;">
                                <span>Entire Map</span>
                            </label>
                            <label style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                                <input type="radio" name="optimize-area" value="quadrant" style="cursor: pointer;">
                                <span>By Quadrant</span>
                            </label>
                            <div id="quadrant-selector" style="display: none; margin-left: 24px; margin-top: 12px;">
                                <div id="optimize-quadrant-map" style="margin-bottom: 8px;"></div>
                                <div style="font-size: 11px; color: var(--text-color-secondary); margin-top: 8px;">
                                    Click on the map or use buttons below to select a quadrant
                                </div>
                                <div style="display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap;">
                                    <button class="optimize-quadrant-btn" data-quadrant="1" style="padding: 4px 8px; font-size: 11px; background: #4a90e2; color: white; border: none; border-radius: 3px; cursor: pointer;">Q1</button>
                                    <button class="optimize-quadrant-btn" data-quadrant="2" style="padding: 4px 8px; font-size: 11px; background: #50c878; color: white; border: none; border-radius: 3px; cursor: pointer;">Q2</button>
                                    <button class="optimize-quadrant-btn" data-quadrant="3" style="padding: 4px 8px; font-size: 11px; background: #ff6b6b; color: white; border: none; border-radius: 3px; cursor: pointer;">Q3</button>
                                    <button class="optimize-quadrant-btn" data-quadrant="4" style="padding: 4px 8px; font-size: 11px; background: #f39c12; color: white; border: none; border-radius: 3px; cursor: pointer;">Q4</button>
                                    <button class="optimize-quadrant-btn" data-quadrant="5" style="padding: 4px 8px; font-size: 11px; background: #9b59b6; color: white; border: none; border-radius: 3px; cursor: pointer;">Q5</button>
                                </div>
                                <div id="selected-quadrant-display" style="margin-top: 8px; font-size: 12px; font-weight: 600; color: var(--accent-color);"></div>
                            </div>
                        </div>
                        <p style="margin-bottom: 12px;">Choose optimization mode:</p>
                        <div style="display: flex; flex-direction: column; gap: 12px;">
                            <button id="optimize-best-buildings" class="btn-optimize-action" style="width: 100%; padding: 12px; font-size: 14px;">
                                Optimize with Best Possible Buildings
                            </button>
                            <p style="font-size: 12px; color: var(--text-color-secondary); margin: 0;">
                                Replaces existing buildings with optimal types and optimizes their placement.
                            </p>
                            <button id="optimize-current-buildings" class="btn-optimize-action" style="width: 100%; padding: 12px; font-size: 14px;">
                                Optimize with Current Buildings
                            </button>
                            <p style="font-size: 12px; color: var(--text-color-secondary); margin: 0;">
                                Optimizes placement of existing buildings without changing their types.
                            </p>
                        </div>
                    </div>
                </div>
            `;
            document.body.appendChild(modal);
            
            // Setup close handlers
            const closeBtn = modal.querySelector('.modal-close');
            const overlay = modal.querySelector('.modal-overlay');
            
            const closeModal = () => {
                modal.style.display = 'none';
            };
            
            closeBtn.addEventListener('click', closeModal);
            overlay.addEventListener('click', closeModal);
            
            // Close on Escape key
            const escapeHandler = (e) => {
                if (e.key === 'Escape' && modal.style.display !== 'none') {
                    closeModal();
                    document.removeEventListener('keydown', escapeHandler);
                }
            };
            document.addEventListener('keydown', escapeHandler);
            
            // Setup area selection and quadrant map
            const areaRadios = modal.querySelectorAll('input[name="optimize-area"]');
            const quadrantSelector = document.getElementById('quadrant-selector');
            const quadrantMapContainer = document.getElementById('optimize-quadrant-map');
            let selectedQuadrant = null;
            
            // Generate quadrant map SVG
            const generateQuadrantMap = () => {
                const cellSize = 3;
                const mapWidth = 21 * cellSize;
                const mapHeight = 21 * cellSize;
                
                const quadrantColors = {
                    0: '#333',
                    1: '#4a90e2',
                    2: '#50c878',
                    3: '#ff6b6b',
                    4: '#f39c12',
                    5: '#9b59b6'
                };
                
                let svgContent = `<svg width="${mapWidth}" height="${mapHeight}" viewBox="0 0 ${mapWidth} ${mapHeight}" style="border: 1px solid var(--border-color); cursor: pointer;">`;
                
                for (let i = 0; i < 21; i++) {
                    for (let j = 0; j < 21; j++) {
                        const quadrant = ZONE[i][j];
                        const color = quadrantColors[quadrant] || '#333';
                        const x = j * cellSize;
                        const y = i * cellSize;
                        const opacity = (selectedQuadrant && quadrant === selectedQuadrant) ? 1.0 : (quadrant === 0 ? 0.3 : 0.6);
                        const strokeWidth = (selectedQuadrant && quadrant === selectedQuadrant) ? 0.5 : 0.2;
                        
                        svgContent += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" fill="${color}" stroke="#000" stroke-width="${strokeWidth}" opacity="${opacity}" class="optimize-quadrant-cell" data-quadrant="${quadrant}" style="cursor: ${quadrant > 0 ? 'pointer' : 'default'}"/>`;
                    }
                }
                
                svgContent += '</svg>';
                return svgContent;
            };
            
            // Update quadrant map display
            const updateQuadrantMap = () => {
                if (quadrantMapContainer) {
                    quadrantMapContainer.innerHTML = generateQuadrantMap();
                    
                    // Add click handlers to cells
                    quadrantMapContainer.querySelectorAll('.optimize-quadrant-cell').forEach(cell => {
                        const quadrant = parseInt(cell.getAttribute('data-quadrant'));
                        if (quadrant > 0) {
                            cell.addEventListener('click', () => {
                                selectedQuadrant = quadrant;
                                updateQuadrantMap();
                                updateSelectedDisplay();
                            });
                        }
                    });
                }
            };
            
            // Update selected quadrant display
            const updateSelectedDisplay = () => {
                const display = document.getElementById('selected-quadrant-display');
                if (display) {
                    if (selectedQuadrant) {
                        display.textContent = `Selected: Quadrant ${selectedQuadrant}`;
                    } else {
                        display.textContent = '';
                    }
                }
            };
            
            // Setup quadrant buttons
            modal.querySelectorAll('.optimize-quadrant-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    selectedQuadrant = parseInt(btn.getAttribute('data-quadrant'));
                    updateQuadrantMap();
                    updateSelectedDisplay();
                });
            });
            
            areaRadios.forEach(radio => {
                radio.addEventListener('change', (e) => {
                    if (e.target.value === 'quadrant') {
                        quadrantSelector.style.display = 'block';
                        updateQuadrantMap();
                    } else {
                        quadrantSelector.style.display = 'none';
                        selectedQuadrant = null;
                    }
                });
            });
            
            // Setup option buttons
            document.getElementById('optimize-best-buildings')?.addEventListener('click', () => {
                const selectedArea = modal.querySelector('input[name="optimize-area"]:checked')?.value;
                const quadrant = selectedArea === 'quadrant' ? selectedQuadrant : null;
                if (selectedArea === 'quadrant' && !selectedQuadrant) {
                    this.gameEngine.message = 'Please select a quadrant first';
                    this.updateUI();
                    return;
                }
                closeModal();
                this.handleOptimize(true, quadrant); // true = replace with best buildings
            });
            
            document.getElementById('optimize-current-buildings')?.addEventListener('click', () => {
                const selectedArea = modal.querySelector('input[name="optimize-area"]:checked')?.value;
                const quadrant = selectedArea === 'quadrant' ? selectedQuadrant : null;
                if (selectedArea === 'quadrant' && !selectedQuadrant) {
                    this.gameEngine.message = 'Please select a quadrant first';
                    this.updateUI();
                    return;
                }
                closeModal();
                this.handleOptimize(false, quadrant); // false = keep current buildings
            });
        }
        
        modal.style.display = 'flex';
    }
    
    /**
     * Handle manual optimization
     * @param {boolean} replaceBuildings - If true, replace buildings with optimal types. If false, only optimize placement of current buildings.
     * @param {number|null} quadrant - Quadrant number (1-5) to optimize, or null for entire map
     */
    async handleOptimize(replaceBuildings = true, quadrant = null) {
        if (!this.gameEngine) return;
        
        this.gameEngine.undoRedo.saveState();
        const areaText = quadrant ? `Quadrant ${quadrant}` : "city layout";
        this.gameEngine.message = `Optimizing ${areaText}...`;
        this.updateUI();
        
        // Yield to browser to show message
        await new Promise(resolve => setTimeout(resolve, 10));
        
        let totalImprovements = 0;
        
        if (replaceBuildings) {
            // Option 1: Optimize with best possible buildings (replace existing)
            
            // Step 1: Remove negative resource nodes
            this.gameEngine.message = `Removing negative resource nodes${quadrant ? ` in Quadrant ${quadrant}` : ''}...`;
            this.updateUI();
            await new Promise(resolve => setTimeout(resolve, 10));
            const removedNodes = quadrant 
                ? this.gameEngine.removeNegativeResourceNodesByQuadrant(quadrant)
                : this.gameEngine.removeNegativeResourceNodes();
            if (removedNodes > 0) {
                totalImprovements += removedNodes;
            }
            
            // Step 2: Fine-tune placements (replaces buildings with optimal types)
            this.gameEngine.message = `Fine-tuning building placements${quadrant ? ` in Quadrant ${quadrant}` : ''}...`;
            this.updateUI();
            await new Promise(resolve => setTimeout(resolve, 10));
            const fineTuneImprovements = quadrant 
                ? this.gameEngine.fineTuneByQuadrant(quadrant, 200)
                : this.gameEngine.fineTune(200);
            totalImprovements += fineTuneImprovements;
            
            // Step 3: Swap buildings for better arrangements
            this.gameEngine.message = `Optimizing building arrangements${quadrant ? ` in Quadrant ${quadrant}` : ''}...`;
            this.updateUI();
            await new Promise(resolve => setTimeout(resolve, 10));
            const swaps = quadrant 
                ? this.gameEngine.swapBuildingsByQuadrant(quadrant)
                : this.gameEngine.swapBuildings();
            totalImprovements += swaps;
            
            // Step 4: Final fine-tune pass
            this.gameEngine.message = `Final optimization pass${quadrant ? ` in Quadrant ${quadrant}` : ''}...`;
            this.updateUI();
            await new Promise(resolve => setTimeout(resolve, 10));
            const finalImprovements = quadrant 
                ? this.gameEngine.fineTuneByQuadrant(quadrant, 100)
                : this.gameEngine.fineTune(100);
            totalImprovements += finalImprovements;
        } else {
            // Option 2: Optimize with current buildings (only swap/move, don't replace or remove)
            
            // Step 1: Move buildings to optimal positions across the entire map
            this.gameEngine.message = `Moving buildings to optimal positions${quadrant ? ` in Quadrant ${quadrant}` : ''}...`;
            this.updateUI();
            await new Promise(resolve => setTimeout(resolve, 10));
            const moves = quadrant 
                ? this.gameEngine.moveBuildingsToOptimalPositionsByQuadrant(quadrant)
                : this.gameEngine.moveBuildingsToOptimalPositions();
            totalImprovements += moves;
            
            // Step 2: Fine-tune with adjacent swaps for final optimization
            this.gameEngine.message = `Fine-tuning placements${quadrant ? ` in Quadrant ${quadrant}` : ''}...`;
            this.updateUI();
            await new Promise(resolve => setTimeout(resolve, 10));
            const swaps = quadrant 
                ? this.gameEngine.swapBuildingsByQuadrant(quadrant)
                : this.gameEngine.swapBuildings();
            totalImprovements += swaps;
        }
        
        // Update totals and show result
        this.gameEngine.updateTotals();
        
        if (totalImprovements > 0) {
            this.gameEngine.message = `Optimization complete! ${totalImprovements} improvements made.`;
        } else {
            this.gameEngine.message = "Optimization complete! No changes needed.";
        }
        
        this.updateUI();
    }
    
    /**
     * Handle import
     */
    async handleImport(text, terrain = null) {
        try {
            // Determine final terrain - use provided terrain or detect from string
            let finalTerrain;
            if (terrain) {
                finalTerrain = terrain;
            } else {
                // Auto-detect from string
                if (text.includes('ShareString')) {
                    // ShareString format: ';' = water, ':' = land
                    finalTerrain = text.includes('[ShareString') && text.charAt(text.indexOf('[ShareString') + 12) === ';' ? 'water' : 'land';
                } else {
                    // ShareLink format: 'W' = water, 'L' = land
                    const firstChar = text.trim().charAt(0);
                    finalTerrain = firstChar === 'W' ? 'water' : 'land';
                }
            }
            
            const isWater = finalTerrain === 'water';
            
            // IMPORTANT: Set terrain BEFORE loading the map
            // This ensures the base map (landMap vs waterMap) is correct before buildings are placed
            console.log('Setting terrain before load:', finalTerrain, 'isWater:', isWater);
            this.gameEngine.water = isWater;
            this.gameEngine.updateWaterTiles(isWater);
            
            // Update canvas terrain BEFORE loading
            if (this.gridCanvas) {
                this.gridCanvas.setWater(isWater);
                console.log('Canvas water set to:', this.gridCanvas.water);
            }
            
            // Now load the layout with terrain override
            this.gameEngine.loadFromString(text, true, isWater);
            
            // Verify terrain after loading
            console.log('Terrain after loadFromString:', this.gameEngine.water, 'expected:', isWater);
            
            // Ensure terrain is still set after loading (loadFromString might have changed it)
            if (this.gameEngine.water !== isWater) {
                console.warn('Terrain was changed by loadFromString, correcting...');
                this.gameEngine.water = isWater;
                this.gameEngine.updateWaterTiles(isWater);
            }
            
            this.gameEngine.updateTotals();
            
            // Search for layout in database and populate City Information if found
            if (this.buildingList) {
                await this.searchAndPopulateLayoutMetadata(text);
            }
            
            // Update terrain toggle buttons and settings
            document.querySelectorAll('.terrain-btn').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.terrain === finalTerrain);
            });
            settings.set('water', isWater);
            
            // Force canvas update to ensure terrain is visually applied
            if (this.gridCanvas) {
                // Use setWater to ensure proper update
                this.gridCanvas.setWater(isWater);
                console.log('Final canvas water:', this.gridCanvas.water);
                // Force full re-render to ensure background layer is updated
                this.gridCanvas.renderSync();
            }
            
            this.updateUI();
            console.log('City imported successfully with terrain:', finalTerrain, 'isWater:', isWater);
        } catch (error) {
            alert('Failed to import city: ' + error.message);
            console.error('Import error:', error);
        }
    }
    
    /**
     * Handle loading layout from various input formats
     * Supports: URLs (cotgopt.com/CODE, louopt.com/CODE), ShortCode, ShortLink, LongLink, Sharestring
     */
    async handleLoadLayout(input) {
        if (!input || !input.trim()) {
            this.gameEngine.message = 'Please enter a link or code';
            this.updateStatusMessage();
            return;
        }
        
        const trimmedInput = input.trim();
        const loadLayoutBtn = document.getElementById('load-layout-btn');
        const loadLayoutInput = document.getElementById('load-layout-input');
        
        // Show loading state
        if (loadLayoutBtn) {
            loadLayoutBtn.disabled = true;
            loadLayoutBtn.textContent = 'Loading...';
        }
        
        try {
            // Determine API base path
            let apiBase = '/api/layouts/';
            const scriptPath = window.location.pathname;
            if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
            } else if (scriptPath.includes('/cotg/')) {
                apiBase = '/cotg/api/layouts/';
            }
            
            let layout = null;
            let layoutString = null;
            
            // Check if it's a URL
            if (trimmedInput.includes('http://') || trimmedInput.includes('https://') || trimmedInput.includes('cotgopt.com') || trimmedInput.includes('louopt.com')) {
                // Extract code from URL patterns
                const urlPatterns = [
                    /\/api\/(maps|layouts)\/short\/([^\/\?]+)/i,
                    /\/([A-Za-z0-9]{1,9})(?:\?|$|\/)/i
                ];
                
                let extractedCode = null;
                for (const pattern of urlPatterns) {
                    const match = trimmedInput.match(pattern);
                    if (match) {
                        extractedCode = match[match.length - 1];
                        break;
                    }
                }
                
                if (!extractedCode) {
                    // Try to extract from end of URL
                    const parts = trimmedInput.split('/').filter(p => p);
                    if (parts.length > 0) {
                        const lastPart = parts[parts.length - 1].split('?')[0];
                        if (lastPart.length >= 1 && lastPart.length <= 9 && /^[a-zA-Z0-9]+$/.test(lastPart)) {
                            extractedCode = lastPart;
                        }
                    }
                }
                
                if (extractedCode) {
                    // Treat as ShortCode if 4 chars, otherwise ShortLink
                    if (extractedCode.length === 4) {
                        // Try ShortCode first
                        const response = await fetch(`${apiBase}get-by-shortcode.php?shortCode=${encodeURIComponent(extractedCode)}`);
                        const data = await response.json();
                        if (data.success && data.layout) {
                            layout = data.layout;
                        }
                    }
                    
                    // If not found or not 4 chars, try ShortLink
                    if (!layout && extractedCode.length >= 1 && extractedCode.length <= 9) {
                        // Try decode endpoint (for ShortLink) - try both methods
                        try {
                            // First try OG-CoTG method (new/better)
                            const decodeResponse = await fetch(`${apiBase}decode.php?code=${encodeURIComponent(extractedCode)}&method=og-cotg`);
                            const decodeData = await decodeResponse.json();
                            if (decodeData.success && decodeData.found && decodeData.layout) {
                                layout = decodeData.layout;
                            } else {
                                // Try LOU method (old) if OG-CoTG didn't find it
                                const decodeResponse2 = await fetch(`${apiBase}decode.php?code=${encodeURIComponent(extractedCode)}&method=lou`);
                                const decodeData2 = await decodeResponse2.json();
                                if (decodeData2.success && decodeData2.found && decodeData2.layout) {
                                    layout = decodeData2.layout;
                                }
                            }
                        } catch (e) {
                            console.log('Decode failed, trying maps/short endpoint');
                        }
                        
                        // If still not found, try maps/short endpoint
                        if (!layout) {
                            try {
                                const mapsApiBase = apiBase.replace('/layouts/', '/maps/');
                                const shortResponse = await fetch(`${mapsApiBase}short.php?code=${encodeURIComponent(extractedCode)}`);
                                if (shortResponse.ok) {
                                    const shortData = await shortResponse.json();
                                    if (shortData.layout) {
                                        layout = shortData.layout;
                                    }
                                }
                            } catch (e) {
                                console.log('Maps short endpoint failed');
                            }
                        }
                    }
                }
            } else {
                // Not a URL - check format
                // Check if it's a ShareString
                if (trimmedInput.includes('[ShareString')) {
                    // Search by ShareString
                    const response = await fetch(`${apiBase}find.php?sharestring=${encodeURIComponent(trimmedInput)}`);
                    const data = await response.json();
                    if (data.success && data.layout) {
                        layout = data.layout;
                    } else {
                        // If not found in DB, try to load directly
                        layoutString = trimmedInput;
                    }
                }
                // Check if it's a LongLink (starts with W or L)
                else if (trimmedInput.length > 0 && (trimmedInput[0] === 'W' || trimmedInput[0] === 'L')) {
                    // Search by LongLink
                    const response = await fetch(`${apiBase}find.php?longLink=${encodeURIComponent(trimmedInput)}`);
                    const data = await response.json();
                    if (data.success && data.layout) {
                        layout = data.layout;
                    } else {
                        // If not found in DB, try to load directly
                        layoutString = trimmedInput;
                    }
                }
                // Check if it's a ShortCode (4 chars)
                else if (trimmedInput.length === 4 && /^[a-zA-Z0-9]{4}$/.test(trimmedInput)) {
                    const response = await fetch(`${apiBase}get-by-shortcode.php?shortCode=${encodeURIComponent(trimmedInput)}`);
                    const data = await response.json();
                    if (data.success && data.layout) {
                        layout = data.layout;
                    }
                }
                // Check if it's a ShortLink (1-9 chars)
                else if (trimmedInput.length >= 1 && trimmedInput.length <= 9 && /^[a-zA-Z0-9]+$/.test(trimmedInput)) {
                    // Try decode endpoint - try both methods (OG-CoTG and LOU)
                    try {
                        // First try OG-CoTG method (new/better)
                        const decodeResponse = await fetch(`${apiBase}decode.php?code=${encodeURIComponent(trimmedInput)}&method=og-cotg`);
                        const decodeData = await decodeResponse.json();
                        if (decodeData.success && decodeData.found && decodeData.layout) {
                            layout = decodeData.layout;
                        } else {
                            // Try LOU method (old) if OG-CoTG didn't find it
                            const decodeResponse2 = await fetch(`${apiBase}decode.php?code=${encodeURIComponent(trimmedInput)}&method=lou`);
                            const decodeData2 = await decodeResponse2.json();
                            if (decodeData2.success && decodeData2.found && decodeData2.layout) {
                                layout = decodeData2.layout;
                            }
                        }
                    } catch (e) {
                        console.log('Decode failed');
                    }
                }
            }
            
            // Load the layout
            if (layout) {
                // Get layout string (prioritize LongLink over Sharestring)
                layoutString = layout.LongLink || layout.Sharestring || '';
                
                if (layoutString) {
                    this.gameEngine.undoRedo.saveState();
                    // Use loadFromString which auto-detects ShareString vs ShareLink format
                    this.gameEngine.loadFromString(layoutString, true, null);
                    this.gameEngine.updateTotals();
                    
                    // Load troops if available
                    if (layout.TroopNumbers && this.militaryPanel) {
                        try {
                            const troopNumbers = JSON.parse(layout.TroopNumbers);
                            if (Array.isArray(troopNumbers) && troopNumbers.some(count => count > 0)) {
                                this.militaryPanel.setTroopNumbersArray(troopNumbers);
                            }
                        } catch (e) {
                            console.log('Failed to parse troop numbers:', e);
                        }
                    }
                    
                    // Load resources if available
                    if (layout.LayoutResources && this.resourcePanel) {
                        try {
                            const layoutResources = JSON.parse(layout.LayoutResources);
                            if (Array.isArray(layoutResources) && layoutResources.length >= 23) {
                                this.resourcePanel.setLayoutResources(layoutResources);
                            }
                        } catch (e) {
                            console.log('Failed to parse layout resources:', e);
                        }
                    }
                    
                    // Set layout metadata
                    if (this.cityInformation) {
                        this.cityInformation.setLayoutMetadata({
                            id: layout.id || '',
                            LayoutName: layout.LayoutName || '',
                            ShortLink: layout.ShortLink || '',
                            ShortCode: layout.ShortCode || '',
                            LongLink: layout.LongLink || '',
                            Sharestring: layout.Sharestring || '',
                            Creator: layout.Creator || '',
                            WaLa: layout.WaLa || '',
                            mapType: layout.mapType || '',
                            Remarks: layout.Remarks || '',
                            Notes: layout.Notes || '',
                            private: layout.private || 0
                        });
                    }
                    
                    this.updateUI();
                    this.gameEngine.message = `Layout loaded: ${layout.LayoutName || 'Unnamed'}`;
                    this.updateStatusMessage();
                    
                    // Clear input
                    if (loadLayoutInput) {
                        loadLayoutInput.value = '';
                    }
                } else {
                    this.gameEngine.message = 'Layout found but has no layout data';
                    this.updateStatusMessage();
                }
            } else if (layoutString) {
                // Load directly from string (not in database)
                this.gameEngine.undoRedo.saveState();
                this.gameEngine.loadFromShareString(layoutString, true, null);
                this.gameEngine.updateTotals();
                this.updateUI();
                this.gameEngine.message = 'Layout loaded from string';
                this.updateStatusMessage();
                
                // Try to find metadata
                await this.searchAndPopulateLayoutMetadata(layoutString);
                
                // Clear input
                if (loadLayoutInput) {
                    loadLayoutInput.value = '';
                }
            } else {
                this.gameEngine.message = 'Layout not found. Please check the link or code.';
                this.updateStatusMessage();
            }
        } catch (error) {
            console.error('Failed to load layout:', error);
            this.gameEngine.message = `Failed to load layout: ${error.message}`;
            this.updateStatusMessage();
        } finally {
            // Restore button state
            if (loadLayoutBtn) {
                loadLayoutBtn.disabled = false;
                loadLayoutBtn.textContent = 'Load Layout';
            }
        }
    }
    
    /**
     * Search for layout in database by ShareString or LongLink and populate City Information
     */
    async searchAndPopulateLayoutMetadata(layoutString) {
        if (!layoutString || !this.cityInformation) return;
        
        try {
            // Determine API base path
            let apiBase = '/api/layouts/';
            const scriptPath = window.location.pathname;
            if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
            }
            
            // Determine if it's ShareString or ShareLink format
            let sharestring = null;
            let longLink = null;
            
            if (layoutString.includes('[ShareString')) {
                // It's a ShareString format
                sharestring = layoutString.trim();
            } else if (layoutString.length > 0 && (layoutString[0] === 'W' || layoutString[0] === 'L')) {
                // It's a ShareLink format
                longLink = layoutString.trim();
            }
            
            // Build search URL
            const params = new URLSearchParams();
            if (sharestring) {
                params.append('sharestring', sharestring);
            }
            if (longLink) {
                params.append('longLink', longLink);
            }
            
            if (params.toString()) {
                const response = await fetch(`${apiBase}find.php?${params}`);
                const data = await response.json();
                
                if (data.success && data.found && data.layout) {
                    // Populate City Information section with found layout
                    this.cityInformation.setLayoutMetadata({
                        id: data.layout.id || '',
                        LayoutName: data.layout.LayoutName || '',
                        ShortLink: data.layout.ShortLink || '',
                        LongLink: data.layout.LongLink || '',
                        Sharestring: data.layout.Sharestring || '',
                        Creator: data.layout.Creator || '',
                        WaLa: data.layout.WaLa || '',
                        mapType: data.layout.mapType || '',
                        Remarks: data.layout.Remarks || '',
                        Notes: data.layout.Notes || ''
                    });
                } else {
                    // Layout not found in database, clear metadata
                    this.cityInformation.clearLayoutMetadata();
                }
            }
        } catch (error) {
            console.log('Failed to search for layout metadata:', error);
            // Don't show error to user, just log it
            if (this.cityInformation) {
                this.cityInformation.clearLayoutMetadata();
            }
        }
    }
    
    /**
     * Handle export
     */
    handleExport(format) {
        if (format === 'sharestring') {
            return this.gameEngine.exportShareString();
        } else {
            let shareLink = this.gameEngine.exportShareLink();
            
            // Append ShortCode if resources or troops exist
            if (this.cityInformation && this.cityInformation.currentLayout) {
                const shortCode = this.cityInformation.currentLayout.ShortCode;
                const hasResources = this.resourcePanel && this.resourcePanel.getLayoutResourcesArray && 
                                   this.resourcePanel.getLayoutResourcesArray() && 
                                   this.resourcePanel.getLayoutResourcesArray().some((val, idx) => idx >= 5 && idx <= 8 && val > 0);
                const hasTroops = this.militaryPanel && this.militaryPanel.getTroopNumbersArray && 
                                this.militaryPanel.getTroopNumbersArray() && 
                                this.militaryPanel.getTroopNumbersArray().some(count => count > 0);
                
                // Append ShortCode if we have resources or troops and a ShortCode exists
                if (shortCode && shortCode.length === 4 && (hasResources || hasTroops)) {
                    shareLink += shortCode;
                }
            }
            
            return shareLink;
        }
    }
    
    /**
     * Load city from URL parameters
     */
    async loadFromURL() {
        const params = new URLSearchParams(window.location.search);
        const mapParam = params.get('map');
        const layoutId = params.get('layoutId');
        let shortCode = params.get('shortCode');
        
        // If no shortCode in URL params, check if pathname is a 4-character code (from .htaccess rewrite)
        // When .htaccess rewrites /L8xi to index.php?shortCode=L8xi, the pathname might still be /L8xi
        // but the query string should have shortCode. However, if it's missing, try to extract from pathname
        if (!shortCode || shortCode.length !== 4) {
            const pathname = window.location.pathname;
            // Check if pathname is exactly a 4-character alphanumeric code
            const pathMatch = pathname.match(/^\/([a-zA-Z0-9]{4})\/?$/);
            if (pathMatch && pathMatch[1]) {
                shortCode = pathMatch[1];
                console.log(`Extracted ShortCode from pathname: ${shortCode}`);
            }
        }
        
        console.log(`loadFromURL: shortCode=${shortCode}, mapParam=${mapParam ? 'present' : 'none'}, layoutId=${layoutId || 'none'}`);
        
        // Handle 4-digit short code (e.g., cotgopt.com/XXXX)
        if (shortCode && shortCode.length === 4) {
            try {
                // Determine API base path - use helper function for consistency
                const apiBase = this.getApiBase() + 'layouts/';
                
                console.log(`Loading layout by ShortCode: ${shortCode}, API base: ${apiBase}, URL: ${window.location.href}`);
                
                const response = await fetch(`${apiBase}get-by-shortcode.php?shortCode=${encodeURIComponent(shortCode)}`);
                
                // Parse response even if status is not OK (404 might return JSON with error details)
                let data;
                try {
                    const responseText = await response.text();
                    data = JSON.parse(responseText);
                } catch (e) {
                    // If response isn't JSON, create error object
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    throw new Error('Invalid JSON response from server');
                }
                
                console.log('ShortCode API response:', data);
                
                // Check if response indicates layout not found (404 or success:false)
                if (!response.ok || !data.success || !data.found) {
                    const errorMsg = data.error || `Layout not found for short code: ${shortCode}`;
                    console.warn(`ShortCode lookup failed: ${errorMsg}`);
                    this.gameEngine.message = errorMsg;
                    // Don't throw error - just log and continue (allow other URL params to be processed)
                    return; // Exit early but don't throw
                }
                
                if (data.success && data.layout) {
                    const layout = data.layout;
                    // Load the layout string (prioritize LongLink over Sharestring)
                    let layoutString = layout.LongLink || layout.Sharestring || '';
                    
                    // Strip any existing appended ShortCode from the layout string
                    // This ensures we don't have duplicate ShortCodes
                    if (layoutString) {
                        const firstChar = layoutString.charAt(0);
                        const isWater = firstChar === 'W' || firstChar === 'w';
                        const expectedLength = isWater ? 287 : 294;
                        
                        // If string is exactly 4 chars longer than expected, check if last 4 chars are alphanumeric
                        if (layoutString.length === expectedLength + 4) {
                            const lastFour = layoutString.substring(expectedLength);
                            if (/^[A-Za-z0-9]{4}$/.test(lastFour)) {
                                // Likely an appended ShortCode, strip it
                                layoutString = layoutString.substring(0, expectedLength);
                                console.log(`Stripped appended ShortCode from stored LongLink: ${lastFour}`);
                            }
                        }
                    }
                    
                    if (layoutString) {
                        this.gameEngine.undoRedo.saveState();
                        // Use loadFromString which auto-detects ShareString vs ShareLink format
                        this.gameEngine.loadFromString(layoutString, true, null);
                        this.gameEngine.updateTotals();
                        
                        // Load troops if available
                        if (layout.TroopNumbers && this.militaryPanel) {
                            try {
                                const troopNumbers = JSON.parse(layout.TroopNumbers);
                                if (Array.isArray(troopNumbers) && troopNumbers.some(count => count > 0)) {
                                    this.militaryPanel.setTroopNumbersArray(troopNumbers);
                                }
                            } catch (e) {
                                console.log('Failed to parse troop numbers:', e);
                            }
                        }
                        
                        // Load resources if available
                        if (layout.LayoutResources && this.resourcePanel) {
                            try {
                                const layoutResources = JSON.parse(layout.LayoutResources);
                                if (Array.isArray(layoutResources) && layoutResources.length >= 23) {
                                    this.resourcePanel.setLayoutResources(layoutResources);
                                }
                            } catch (e) {
                                console.log('Failed to parse layout resources:', e);
                            }
                        }
                        
                        // Set layout metadata
                        if (this.cityInformation) {
                            this.cityInformation.setLayoutMetadata({
                                id: layout.id || '',
                                LayoutName: layout.LayoutName || '',
                                ShortLink: layout.ShortLink || '',
                                ShortCode: layout.ShortCode || '',
                                LongLink: layout.LongLink || '',
                                Sharestring: layout.Sharestring || '',
                                Creator: layout.Creator || '',
                                WaLa: layout.WaLa || '',
                                mapType: layout.mapType || '',
                                Remarks: layout.Remarks || '',
                                Notes: layout.Notes || '',
                                private: layout.private || 0
                            });
                        }
                        
                        this.updateUI();
                        this.gameEngine.message = `City loaded from short code: ${shortCode}`;
                        return; // Exit early, don't process mapParam
                    } else {
                        console.error('Layout found but missing layout data for short code:', shortCode);
                        this.gameEngine.message = `Layout found but missing layout data for short code: ${shortCode}`;
                        // Exit early - don't try other loading methods if we found the layout but it's invalid
                        return;
                    }
                }
            } catch (error) {
                console.error('Failed to load layout by short code:', error);
                this.gameEngine.message = `Failed to load layout: ${error.message}`;
                // Exit early on error - don't try other loading methods
                return;
            }
        }
        
        // If layoutId is provided, load full layout data (including resources/troops)
        if (layoutId) {
            try {
                // Determine API base path
                let apiBase = '/api/layouts/';
                const scriptPath = window.location.pathname;
                if (scriptPath.includes('/Louopt/')) {
                    apiBase = '/Louopt/api/layouts/';
                } else if (scriptPath.includes('/cotgopt/')) {
                    apiBase = '/cotgopt/api/layouts/';
                } else if (scriptPath.includes('/cotg/')) {
                    apiBase = '/cotg/api/layouts/';
                }
                
                const response = await fetch(`${apiBase}get.php?id=${encodeURIComponent(layoutId)}`);
                const data = await response.json();
                
                if (data.success && data.layout) {
                    const layout = data.layout;
                    // Load the layout string (prioritize LongLink over Sharestring)
                    const layoutString = layout.LongLink || layout.Sharestring || '';
                    
                    if (layoutString) {
                        this.gameEngine.undoRedo.saveState();
                        // Use loadFromString which auto-detects ShareString vs ShareLink format
                        this.gameEngine.loadFromString(layoutString, true, null);
                        this.gameEngine.updateTotals();
                        
                        // Load troops if available
                        if (layout.TroopNumbers && this.militaryPanel) {
                            try {
                                const troopNumbers = JSON.parse(layout.TroopNumbers);
                                if (Array.isArray(troopNumbers) && troopNumbers.some(count => count > 0)) {
                                    this.militaryPanel.setTroopNumbersArray(troopNumbers);
                                }
                            } catch (e) {
                                console.log('Failed to parse troop numbers:', e);
                            }
                        }
                        
                        // Load resources if available
                        if (layout.LayoutResources && this.resourcePanel) {
                            try {
                                const layoutResources = JSON.parse(layout.LayoutResources);
                                if (Array.isArray(layoutResources) && layoutResources.length >= 23) {
                                    this.resourcePanel.setLayoutResources(layoutResources);
                                }
                            } catch (e) {
                                console.log('Failed to parse layout resources:', e);
                            }
                        }
                        
                        // Set layout metadata
                        if (this.cityInformation) {
                            this.cityInformation.setLayoutMetadata({
                                id: layout.id || '',
                                LayoutName: layout.LayoutName || '',
                                ShortLink: layout.ShortLink || '',
                                ShortCode: layout.ShortCode || '',
                                LongLink: layout.LongLink || '',
                                Sharestring: layout.Sharestring || '',
                                Creator: layout.Creator || '',
                                WaLa: layout.WaLa || '',
                                mapType: layout.mapType || '',
                                Remarks: layout.Remarks || '',
                                Notes: layout.Notes || '',
                                private: layout.private || 0
                            });
                        }
                        
                        this.updateUI();
                        this.gameEngine.message = `City loaded from layout ID: ${layoutId}`;
                        return; // Exit early, don't process mapParam
                    }
                } else {
                    console.error('Layout not found for ID:', layoutId);
                    this.gameEngine.message = `Layout not found for ID: ${layoutId}`;
                }
            } catch (error) {
                console.error('Failed to load layout by ID:', error);
                this.gameEngine.message = `Failed to load layout: ${error.message}`;
            }
        }
        
        if (mapParam) {
            try {
                // URLSearchParams.get() automatically decodes the parameter
                // But we need to ensure the ShareString format is intact
                // Remove any newlines from the map parameter (loadFromShareString strips whitespace anyway)
                let cleanMapParam = mapParam.replace(/\r?\n/g, '').trim();
                
                // Debug: Log the first character and length for ShareLink format (W/L)
                if (cleanMapParam.length > 0 && (cleanMapParam.charAt(0) === 'W' || cleanMapParam.charAt(0) === 'w' || cleanMapParam.charAt(0) === 'L' || cleanMapParam.charAt(0) === 'l')) {
                    console.log(`Loading ShareLink from URL: length=${cleanMapParam.length}, firstChar='${cleanMapParam.charAt(0)}', isWater=${cleanMapParam.charAt(0) === 'W' || cleanMapParam.charAt(0) === 'w'}`);
                }
                
                // Debug: Log if it's a ShareString format
                if (cleanMapParam.includes('ShareString')) {
                    console.log('Loading ShareString from URL, length:', cleanMapParam.length);
                    // Verify the format is correct
                    if (!cleanMapParam.match(/\[ShareString\.1\.3\]/)) {
                        console.warn('ShareString format may be corrupted in URL');
                    }
                }
                
                // Load the layout string first (this works even without shortCode)
                // Clear map first to remove any old buildings/nodes
                this.gameEngine.loadFromString(cleanMapParam, true);
                this.gameEngine.updateTotals();
                this.updateUI();
                this.gameEngine.message = "City loaded from URL.";
                
                // Check for ShortCode in URL parameter or appended to map string
                let shortCodeParam = params.get('shortCode');
                
                // If no shortCode in URL, check if one was appended to the map string
                if (!shortCodeParam && this.gameEngine.appendedShortCode) {
                    shortCodeParam = this.gameEngine.appendedShortCode;
                    console.log(`Using appended ShortCode from map string: ${shortCodeParam}`);
                    // Clear it after using it
                    this.gameEngine.appendedShortCode = null;
                }
                
                // If shortCode is provided (from URL or appended), use it to load full layout data (including troops/resources)
                if (shortCodeParam && shortCodeParam.length === 4) {
                    try {
                        // Determine API base path
                        let apiBase = '/api/layouts/';
                        const scriptPath = window.location.pathname;
                        if (scriptPath.includes('/Louopt/')) {
                            apiBase = '/Louopt/api/layouts/';
                        } else if (scriptPath.includes('/cotgopt/')) {
                            apiBase = '/cotgopt/api/layouts/';
                        } else if (scriptPath.includes('/cotg/')) {
                            apiBase = '/cotg/api/layouts/';
                        }
                        
                        const response = await fetch(`${apiBase}get-by-shortcode.php?shortCode=${encodeURIComponent(shortCodeParam)}`);
                        const data = await response.json();
                        
                        if (data.success && data.layout) {
                            const layout = data.layout;
                            
                            // Load troops if available
                            if (layout.TroopNumbers && this.militaryPanel) {
                                try {
                                    const troopNumbers = JSON.parse(layout.TroopNumbers);
                                    if (Array.isArray(troopNumbers) && troopNumbers.some(count => count > 0)) {
                                        this.militaryPanel.setTroopNumbersArray(troopNumbers);
                                    }
                                } catch (e) {
                                    console.log('Failed to parse troop numbers:', e);
                                }
                            }
                            
                            // Load resources if available
                            if (layout.LayoutResources && this.resourcePanel) {
                                try {
                                    const layoutResources = JSON.parse(layout.LayoutResources);
                                    if (Array.isArray(layoutResources) && layoutResources.length >= 23) {
                                        this.resourcePanel.setLayoutResources(layoutResources);
                                    }
                                } catch (e) {
                                    console.log('Failed to parse layout resources:', e);
                                }
                            }
                            
                            // Set layout metadata
                            if (this.cityInformation) {
                                this.cityInformation.setLayoutMetadata({
                                    id: layout.id || '',
                                    LayoutName: layout.LayoutName || '',
                                    ShortLink: layout.ShortLink || '',
                                    ShortCode: layout.ShortCode || '',
                                    LongLink: layout.LongLink || '',
                                    Sharestring: layout.Sharestring || '',
                                    Creator: layout.Creator || '',
                                    WaLa: layout.WaLa || '',
                                    mapType: layout.mapType || '',
                                    Remarks: layout.Remarks || '',
                                    Notes: layout.Notes || '',
                                    private: layout.private || 0
                                });
                            }
                            
                            this.updateUI();
                            return; // Exit early, don't process layoutId
                        }
                    } catch (error) {
                        console.log('Failed to load layout by short code:', error);
                        // Continue with layoutId fallback
                    }
                }
                
                // If no shortCode is provided, don't load any metadata (Remarks, Notes, troops, resources)
                // Without shortCode, we can't uniquely identify which layout entry this is
                // if multiple layouts share the same LongLink, so we only load the basic layout structure
                // The city layout has already been loaded from the LongLink above
                
                if (this.cityInformation && !shortCodeParam) {
                    // Clear metadata if no shortCode - we only have the layout structure, not the database entry
                    this.cityInformation.clearLayoutMetadata();
                }
            } catch (error) {
                console.error('Failed to load city from URL:', error);
                this.gameEngine.message = `Failed to load city from URL: ${error.message}`;
                this.updateUI();
            }
        } else if (this.cityInformation) {
            // Clear metadata if no map parameter
            this.cityInformation.clearLayoutMetadata();
        }
    }
    
    /**
     * Update all UI components
     */
    updateUI() {
        this.gridCanvas.update();
        
        
        if (this.resourcePanel) {
            this.resourcePanel.update();
        }
        if (this.militaryPanel) {
            this.militaryPanel.update();
        }
        if (this.buildingList) {
            this.buildingList.update();
        }
        if (this.optimalBuilding && this.gridCanvas.hoverTile) {
            this.optimalBuilding.update(this.gridCanvas.hoverTile.i, this.gridCanvas.hoverTile.j);
        }
        this.updateStatusMessage();
        if (this.originalTilePreview && this.gridCanvas.hoverTile) {
            this.originalTilePreview.update(this.gridCanvas.hoverTile.i, this.gridCanvas.hoverTile.j);
        }
        if (this.buildingStats && this.gridCanvas.hoverTile) {
            this.buildingStats.update(this.gridCanvas.hoverTile.i, this.gridCanvas.hoverTile.j);
        }
        if (this.keyboardShortcutsDisplay) {
            this.keyboardShortcutsDisplay.render();
        }
        this.updateCanvasBuildingDisplay();
        this.updateCanvasTotalDisplay();
        this.updateCanvasResourceDisplay();
        this.updateCanvasMilitaryDisplay();
    }
    
    /**
     * Update the building display (Buildings and Build Speed) at the top left of the canvas
     */
    updateCanvasBuildingDisplay() {
        if (!this.gameEngine) return;
        
        const displayEl = document.getElementById('canvas-building-display');
        if (!displayEl) return;
        
        const { gameEngine } = this;
        
        const formatNumber = (num) => Math.round(num).toLocaleString();
        const buildingsValue = `${formatNumber(gameEngine.building_tot)}/${formatNumber(gameEngine.maxBuildings || 100)}`;
        const buildSpeedValue = `${formatNumber(Math.round(gameEngine.building_rate * 100))}%`;
        
        displayEl.innerHTML = `
            <div class="canvas-resource-content">
                <!-- Building Stats -->
                <div class="canvas-resource-section">
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_build_slots.png')}" alt="Buildings" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value ${gameEngine.building_tot > (gameEngine.maxBuildings || 100) ? 'warning' : ''}">${buildingsValue}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_clock_orange.png')}" alt="Build Speed" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${buildSpeedValue}</span>
                    </div>
                </div>
            </div>
        `;
    }
    
    /**
     * Update the total resources display at the top of the canvas (between buildings and resources)
     */
    updateCanvasTotalDisplay() {
        if (!this.gameEngine) return;
        
        const displayEl = document.getElementById('canvas-total-display');
        if (!displayEl) return;
        
        const { gameEngine } = this;
        
        const formatNumber = (num) => Math.round(num).toLocaleString();
        const totalRes = gameEngine.wood_tot + gameEngine.stone_tot + gameEngine.iron_tot + 
                        gameEngine.food_tot + gameEngine.gold_tot;
        
        displayEl.innerHTML = `
            <div class="canvas-resource-content">
                <!-- Total Resources -->
                <div class="canvas-resource-section">
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/star_icon.png')}" alt="Total" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(totalRes)}</span>
                    </div>
                </div>
            </div>
        `;
    }
    
    /**
     * Update the resource display at the top center of the canvas
     */
    updateCanvasResourceDisplay() {
        if (!this.gameEngine) return;
        
        const displayEl = document.getElementById('canvas-resource-display');
        if (!displayEl) return;
        
        const { gameEngine } = this;
        
        const formatNumber = (num) => Math.round(num).toLocaleString();
        
        displayEl.innerHTML = `
            <div class="canvas-resource-content">
                <!-- Resource Production Stats -->
                <div class="canvas-resource-section">
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_resource_wood.png')}" alt="Wood" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.wood_tot)}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_resource_stone.png')}" alt="Stone" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.stone_tot)}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_resource_iron.png')}" alt="Iron" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.iron_tot)}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_resource_grain.png')}" alt="Food" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.food_tot)}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_resource_gold.png')}" alt="Gold" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.gold_tot)}</span>
                    </div>
                </div>
            </div>
        `;
    }
    
    /**
     * Update the military/transport display (Troops, Carts, Ships) at the top right of the canvas
     */
    updateCanvasMilitaryDisplay() {
        if (!this.gameEngine) return;
        
        const displayEl = document.getElementById('canvas-military-display');
        if (!displayEl) return;
        
        const { gameEngine } = this;
        
        const formatNumber = (num) => Math.round(num).toLocaleString();
        
        displayEl.innerHTML = `
            <div class="canvas-resource-content">
                <!-- Military and Transport Stats -->
                <div class="canvas-resource-section">
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_recruit_slots.png')}" alt="Troops" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.troop_capacity_tot)}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_cart_city_info.png')}" alt="Carts" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.cart_tot)}</span>
                    </div>
                    <div class="canvas-resource-item">
                        <img src="${getImagePath('ui/icon_ship_city_info.png')}" alt="Ships" class="canvas-stat-icon" onerror="this.style.display='none'">
                        <span class="canvas-status-value">${formatNumber(gameEngine.ship_tot)}</span>
                    </div>
                </div>
            </div>
        `;
    }
    
    
    /**
     * Handle new city creation
     */
    handleNewCity() {
        if (confirm('Create a new city? This will clear your current city.')) {
            // Clear layout metadata when creating new city
            if (this.cityInformation) {
                this.cityInformation.clearLayoutMetadata();
            }
            // Reset to empty map
            this.gameEngine.map = this.gameEngine.createMapFromTemplate(this.gameEngine.landMap);
            this.gameEngine.origMap = this.gameEngine.createMapFromTemplate(this.gameEngine.landMap);
            this.gameEngine.resetLocks();
            // Reset wheat fields (farm fields)
            this.gameEngine.wheatFields = this.gameEngine.createEmptyMap();
            this.gameEngine.updateTotals();
            this.updateUI();
        }
    }
    
    /**
     * Handle city reset
     */
    handleResetCity() {
        if (confirm('Really revert all changes? This cannot be undone.')) {
            // Reset to original map
            if (this.gameEngine.last_load_string) {
                this.gameEngine.loadFromString(this.gameEngine.last_load_string, true);
            } else {
                // Reset to empty if no saved state
                this.gameEngine.map = this.gameEngine.createMapFromTemplate(this.gameEngine.landMap);
                this.gameEngine.origMap = this.gameEngine.createMapFromTemplate(this.gameEngine.landMap);
            }
            this.gameEngine.updateTotals();
            this.updateUI();
        }
    }
    
    /**
     * Handle import/export dialog
     */
    handleImportExport() {
        if (this.importExportModal) {
            // Update export output
            const exportText = this.handleExport('sharestring');
            this.importExportModal.updateExportOutput(exportText);
            this.importExportModal.open();
        }
    }
    
    /**
     * Handle export to game - copies layout string to clipboard and shows modal with troops/resources
     */
    handleExportToGame() {
        // Get layout string (ShareString format)
        const layoutString = this.handleExport('sharestring');
        
        // Get troops if available
        let troopNumbers = null;
        if (this.militaryPanel && typeof this.militaryPanel.getTroopNumbersArray === 'function') {
            troopNumbers = this.militaryPanel.getTroopNumbersArray();
            // Check if there are any non-zero troops
            if (troopNumbers && !troopNumbers.some(count => count > 0)) {
                troopNumbers = null;
            }
        }
        
        // Get resources if available
        let layoutResources = null;
        if (this.resourcePanel && typeof this.resourcePanel.getLayoutResourcesArray === 'function') {
            layoutResources = this.resourcePanel.getLayoutResourcesArray();
            // Check if there are any non-zero resources (indices 5-8 for request, 19-22 for excess)
            if (layoutResources) {
                const hasRequestResources = layoutResources.slice(5, 9).some(val => val > 0);
                const hasExcessResources = layoutResources.slice(19, 23).some(val => val > 0);
                if (!hasRequestResources && !hasExcessResources) {
                    layoutResources = null;
                }
            }
        }
        
        // Copy layout string to clipboard
        navigator.clipboard.writeText(layoutString).then(() => {
            // Show export modal with layout string and troops/resources info
            this.showExportToGameModal(layoutString, troopNumbers, layoutResources);
        }).catch(err => {
            console.error('Failed to copy to clipboard:', err);
            // Still show modal even if copy fails
            this.showExportToGameModal(layoutString, troopNumbers, layoutResources);
        });
    }
    
    /**
     * Show export to game modal with layout string and optional troops/resources
     */
    showExportToGameModal(layoutString, troopNumbers, layoutResources) {
        // Create or get modal
        if (!this.exportToGameModal) {
            this.exportToGameModal = new Modal('export-to-game-modal', 'Export to Game');
        }
        
        // Build modal content
        let content = `
            <div style="padding: 20px;">
                <p style="margin-bottom: 15px; color: #4a9eff;">
                    <strong>✓ Layout string copied to clipboard!</strong>
                </p>
                <p style="margin-bottom: 15px;">
                    Paste the layout string into the game's overlay textarea, then click "Apply layout".
                </p>
                
                <div style="margin-bottom: 20px;">
                    <label style="display: block; margin-bottom: 5px; font-weight: bold;">Layout String:</label>
                    <textarea id="export-layout-string" readonly style="width: 100%; min-height: 150px; padding: 8px; font-family: monospace; font-size: 11px; background: var(--bg-secondary, #1a1a1a); border: 1px solid var(--border-color, #333); border-radius: 4px; color: var(--text-color, #fff); resize: vertical;">${layoutString}</textarea>
                    <button id="copy-layout-string" style="margin-top: 8px; padding: 6px 12px; background: var(--accent-color, #4a9eff); color: white; border: none; border-radius: 4px; cursor: pointer;">Copy Layout String Again</button>
                </div>
        `;
        
        // Add troops section if available
        if (troopNumbers && troopNumbers.some(count => count > 0)) {
            const unitNames = ['Guard', 'Ballista', 'Ranger', 'Triari', 'Priestess', 'Vanquisher', 'Sorceress', 'Scout', 'Arbalist', 'Praetor', 'Horseman', 'Druid', 'Battering Ram', 'Scorpion', 'Galley', 'Stinger', 'Warship', 'Senator'];
            content += `
                <div style="margin-bottom: 20px; padding: 15px; background: var(--bg-secondary, #1a1a1a); border-radius: 4px; border: 1px solid var(--border-color, #333);">
                    <h3 style="margin-top: 0; margin-bottom: 10px; font-size: 14px;">Troops (for reference):</h3>
                    <p style="margin-bottom: 10px; font-size: 12px; color: #aaa;">
                        If you want to include troops, make sure the "Add Troops" checkbox is checked in the game before applying the layout.
                    </p>
                    <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; font-size: 11px;">
            `;
            troopNumbers.forEach((count, index) => {
                if (count > 0) {
                    content += `
                        <div style="padding: 4px;">
                            <strong>${unitNames[index]}:</strong> ${count.toLocaleString()}
                        </div>
                    `;
                }
            });
            content += `
                    </div>
                    <div style="margin-top: 10px; padding: 8px; background: rgba(74, 158, 255, 0.1); border-radius: 4px; font-size: 11px; font-family: monospace; word-break: break-all;">
                        <strong>Troop Array:</strong> [${troopNumbers.join(',')}]
                    </div>
                </div>
            `;
        }
        
        // Add resources section if available
        if (layoutResources) {
            const hasRequestResources = layoutResources.slice(5, 9).some(val => val > 0);
            const hasExcessResources = layoutResources.slice(19, 23).some(val => val > 0);
            
            if (hasRequestResources || hasExcessResources) {
                const requestWood = layoutResources[5] || 0;
                const requestStone = layoutResources[6] || 0;
                const requestIron = layoutResources[7] || 0;
                const requestFood = layoutResources[8] || 0;
                const excessWood = layoutResources[19] || 0;
                const excessStone = layoutResources[20] || 0;
                const excessIron = layoutResources[21] || 0;
                const excessFood = layoutResources[22] || 0;
                
                content += `
                    <div style="margin-bottom: 20px; padding: 15px; background: var(--bg-secondary, #1a1a1a); border-radius: 4px; border: 1px solid var(--border-color, #333);">
                        <h3 style="margin-top: 0; margin-bottom: 10px; font-size: 14px;">Resources (for reference):</h3>
                        <p style="margin-bottom: 10px; font-size: 12px; color: #aaa;">
                            If you want to include resources, make sure the "Add Resources" checkbox is checked in the game before applying the layout.
                        </p>
                `;
                
                if (hasRequestResources) {
                    content += `
                        <div style="margin-bottom: 10px;">
                            <strong style="font-size: 12px;">Request Resources:</strong>
                            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 5px; font-size: 11px;">
                                <div>Wood: ${requestWood.toLocaleString()}</div>
                                <div>Stone: ${requestStone.toLocaleString()}</div>
                                <div>Iron: ${requestIron.toLocaleString()}</div>
                                <div>Food: ${requestFood.toLocaleString()}</div>
                            </div>
                        </div>
                    `;
                }
                
                if (hasExcessResources) {
                    content += `
                        <div style="margin-bottom: 10px;">
                            <strong style="font-size: 12px;">Excess Resources:</strong>
                            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 5px; font-size: 11px;">
                                <div>Wood: ${excessWood.toLocaleString()}</div>
                                <div>Stone: ${excessStone.toLocaleString()}</div>
                                <div>Iron: ${excessIron.toLocaleString()}</div>
                                <div>Food: ${excessFood.toLocaleString()}</div>
                            </div>
                        </div>
                    `;
                }
                
                content += `
                        <div style="margin-top: 10px; padding: 8px; background: rgba(74, 158, 255, 0.1); border-radius: 4px; font-size: 11px; font-family: monospace; word-break: break-all; max-height: 100px; overflow-y: auto;">
                            <strong>Resource Array:</strong> [${layoutResources.join(',')}]
                        </div>
                    </div>
                `;
            }
        }
        
        content += `
            </div>
        `;
        
        this.exportToGameModal.setContent(content);
        this.exportToGameModal.open();
        
        // Setup copy button
        setTimeout(() => {
            const copyBtn = document.getElementById('copy-layout-string');
            if (copyBtn) {
                copyBtn.addEventListener('click', () => {
                    const textarea = document.getElementById('export-layout-string');
                    if (textarea) {
                        textarea.select();
                        navigator.clipboard.writeText(layoutString).then(() => {
                            copyBtn.textContent = 'Copied!';
                            setTimeout(() => {
                                copyBtn.textContent = 'Copy Layout String Again';
                            }, 2000);
                        });
                    }
                });
            }
        }, 100);
    }
    
    /**
     * Handle terrain change (land/water)
     */
    handleTerrainChange(terrain) {
        document.querySelectorAll('.terrain-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.terrain === terrain);
        });
        
        const isWater = terrain === 'water';
        this.gameEngine.updateWaterTiles(isWater);
        
        // Only update canvas if it's initialized
        if (this.gridCanvas) {
            this.gridCanvas.setWater(isWater);
            this.gridCanvas.update();
        }
        
        settings.set('water', isWater);
    }
    
    /**
     * Update drag and drop toggle button appearance
     */
    updateDragDropToggleButton(button) {
        if (!button || !this.crossContainerDragDrop) return;
        
        const enabled = this.crossContainerDragDrop.isEnabled();
        button.classList.toggle('active', enabled);
        button.title = enabled 
            ? 'Edit Page Layout - Drag and drop enabled (click to disable)' 
            : 'Edit Page Layout - Drag and drop disabled (click to enable)';
    }
    
    /**
     * Initialize rotating text for Share button
     */
    initRotatingShareButton() {
        const button = document.getElementById('btn-import-export');
        const textSpan = button?.querySelector('.rotating-text');
        if (!textSpan) return;
        
        const texts = ['Share', 'Import', 'Export'];
        let currentIndex = 0;
        
        // Rotate text every 2 seconds
        setInterval(() => {
            currentIndex = (currentIndex + 1) % texts.length;
            textSpan.textContent = texts[currentIndex];
        }, 2000);
    }
    
    /**
     * Handle tab switching
     */
    handleTabSwitch(tab) {
        document.querySelectorAll('.tab-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.tab === tab);
        });
        
        document.querySelectorAll('.tab-content').forEach(content => {
            content.classList.toggle('active', content.id === `${tab}-panel`);
        });
    }
    
    /**
     * Get API base path
     */
    getApiBase() {
        const hostname = window.location.hostname;
        const pathname = window.location.pathname;
        
        // Check for root domains: cotg.rlmglobal.com or cotgopt.com (base path is root)
        if (hostname.includes('cotg.rlmglobal.com') || 
            (hostname.includes('cotgopt.com') && !pathname.includes('/cotgopt/'))) {
            return '/api/';
        }
        
        // Check pathname for subdirectories
        // Note: When pathname is a 4-char ShortCode like /L8xi, it won't match subdirectories
        // so it will correctly default to root /api/
        if (pathname.includes('/Louopt/')) {
            return '/Louopt/api/';
        } else if (pathname.includes('/cotgopt/')) {
            return '/cotgopt/api/';
        } else if (pathname.includes('/cotg/')) {
            return '/cotg/api/';
        }
        
        // Default to root API path (correct for root domain like cotgopt.com/L8xi)
        return '/api/';
    }
    
    /**
     * Get base path for links
     */
    getBasePath() {
        const path = window.location.pathname;
        const hostname = window.location.hostname;
        
        // Check for root domains: cotg.rlmglobal.com or cotgopt.com (base path is root)
        if (hostname.includes('cotg.rlmglobal.com') || 
            (hostname.includes('cotgopt.com') && !path.includes('/cotgopt/'))) {
            return '/';
        }
        
        // Remove index.php or other PHP files from the path
        // Also handle ShortCode paths like /L8xi - these should be treated as root
        const cleanPath = path.replace(/\/[^\/]+\.php$/, '').replace(/^\/[a-zA-Z0-9]{4}\/?$/, '');
        
        // Check for common base paths
        if (cleanPath.includes('/Louopt')) {
            return '/Louopt/';
        }
        if (cleanPath.includes('/cotgopt')) {
            return '/cotgopt/';
        }
        if (cleanPath.includes('/cotg/')) {
            // rlmglobal.com/cotg/ - subdirectory
            return '/cotg/';
        }
        
        // Default to root
        return '/';
    }
    
    /**
     * Toggle game mode on/off
     */
    toggleGameMode() {
        this.gameMode = !this.gameMode;
        
        if (this.gameMode) {
            this.enableGameMode();
        } else {
            this.disableGameMode();
        }
        
        // Update button text
        const btn = document.getElementById('btn-game-mode');
        if (btn) {
            btn.textContent = this.gameMode ? '📋 Planner Mode' : '🎮 Game Mode';
            btn.classList.toggle('active', this.gameMode);
        }
    }
    
    /**
     * Enable game mode
     */
    enableGameMode() {
        // Hide planner UI
        const plannerContainer = document.querySelector('.city-planner');
        if (plannerContainer) {
            plannerContainer.style.display = 'none';
        }
        
        // Initialize game state
        if (!this.gameState) {
            this.gameState = new GameState(this.gameEngine);
            this.gameState.initialize();
            
            // Initialize currency manager
            if (this.gameState.currencyManager) {
                this.gameState.currencyManager.initialize().catch(err => {
                    console.error('Failed to initialize currency manager:', err);
                });
            }
            
            // Try to load existing save
            this.gameState.load();
        }
        
        // Initialize game loop
        if (!this.gameLoop) {
            this.gameLoop = new GameLoop(this.gameState);
            this.gameLoop.start();
        }
        
        // Initialize notification system
        if (!this.notificationSystem) {
            this.notificationSystem = new NotificationSystem();
        }
        
        // Initialize new Game UI
        this.gameUIContainer = document.getElementById('game-ui-container');
        if (this.gameUIContainer && !this.gameUI) {
            this.gameUI = new GameUI(this.gameUIContainer, this.gameState, this.gameEngine, {
                onBuildingSelected: (buildingKey) => {
                    this.selectedBuildingKey = buildingKey;
                },
                onTogglePause: () => {
                    if (this.gameState) {
                        if (this.gameState.isPaused) {
                            this.gameState.resume();
                        } else {
                            this.gameState.pause();
                        }
                    }
                },
                onSetSpeed: (speed) => {
                    if (this.gameState) {
                        this.gameState.setSpeed(speed);
                    }
                },
                onCancelConstruction: (queueId) => {
                    if (this.gameState && this.gameState.constructionManager) {
                        this.gameState.constructionManager.cancelConstruction(queueId);
                    }
                },
                onSpeedUpConstruction: async (queueId) => {
                    if (this.gameState && this.gameState.currencyManager) {
                        try {
                            await this.gameState.currencyManager.speedUpConstruction(queueId);
                            this.notificationSystem.success('Construction speeded up!');
                            if (this.gameUI) {
                                this.gameUI.update();
                            }
                        } catch (error) {
                            this.notificationSystem.error(error.message || 'Failed to speed up construction');
                        }
                    }
                },
                onInstantComplete: async (queueId) => {
                    if (this.gameState && this.gameState.currencyManager) {
                        try {
                            await this.gameState.currencyManager.instantCompleteConstruction(queueId);
                            this.notificationSystem.success('Construction completed instantly!');
                            if (this.gameUI) {
                                this.gameUI.update();
                            }
                        } catch (error) {
                            this.notificationSystem.error(error.message || 'Failed to complete construction');
                        }
                    }
                },
                onOpenWorldMap: () => {
                    this.openWorldMap();
                }
            });
            
            this.gameUI.init();
            this.gameUIContainer.style.display = 'flex';
            
            // Move canvas to game UI
            const canvasWrapper = this.gameUI.getCanvasWrapper();
            if (canvasWrapper && this.gridCanvas && this.gridCanvas.canvas) {
                canvasWrapper.appendChild(this.gridCanvas.canvas);
            }
        }
        
        // Listen for game events
        if (this.gameState) {
            this.gameState.on('buildingCompleted', (data) => {
                this.notificationSystem.success(`${this.getBuildingNameForNotification(data.buildingType)} completed!`);
                if (this.gameUI) {
                    this.gameUI.update();
                }
            });
            
            this.gameState.on('resourceUpdated', () => {
                if (this.gameUI) {
                    this.gameUI.update();
                }
            });
            
            this.gameState.on('constructionStarted', () => {
                if (this.gameUI) {
                    this.gameUI.update();
                }
            });
            
            this.gameState.on('currencyUpdated', () => {
                if (this.gameUI) {
                    this.gameUI.update();
                }
            });
        }
        
        // Update UI
        if (this.gameUI) {
            this.gameUI.update();
        }
    }
    
    /**
     * Open world map view
     */
    openWorldMap() {
        // Create modal if it doesn't exist
        if (!this.worldMapModal) {
            this.worldMapModal = document.createElement('div');
            this.worldMapModal.id = 'world-map-modal';
            this.worldMapModal.className = 'world-map-modal';
            this.worldMapModal.innerHTML = `
                <div class="world-map-modal-content">
                    <div class="world-map-modal-header">
                        <h2>World Map</h2>
                        <button class="world-map-close-btn" id="world-map-close">×</button>
                    </div>
                    <div class="world-map-container" id="world-map-container"></div>
                </div>
            `;
            document.body.appendChild(this.worldMapModal);
            
            // Close button handler
            const closeBtn = this.worldMapModal.querySelector('#world-map-close');
            closeBtn.addEventListener('click', () => this.closeWorldMap());
            
            // Close on outside click
            this.worldMapModal.addEventListener('click', (e) => {
                if (e.target === this.worldMapModal) {
                    this.closeWorldMap();
                }
            });
            
            // Close on ESC key
            const escHandler = (e) => {
                if (e.key === 'Escape' && this.worldMapModal && this.worldMapModal.style.display !== 'none') {
                    this.closeWorldMap();
                }
            };
            document.addEventListener('keydown', escHandler);
            this.worldMapEscHandler = escHandler;
        }
        
        // Initialize world map view if needed
        if (!this.worldMapView && this.gameState && this.gameState.worldMapManager) {
            const container = this.worldMapModal.querySelector('#world-map-container');
            this.worldMapView = new WorldMapView(
                container,
                this.gameState.worldMapManager,
                {
                    onCitySelect: (city) => {
                        this.notificationSystem.info(`Selected city: ${city.name}`);
                        // Could navigate to city here
                    },
                    onTileClick: (x, y) => {
                        if (this.worldMapView && this.gameState.worldMapManager.canPlaceCity(x, y)) {
                            // Show city placement dialog
                            this.notificationSystem.info(`Valid location at (${x}, ${y})`);
                        }
                    }
                }
            );
            this.worldMapView.init();
        }
        
        // Show modal
        this.worldMapModal.style.display = 'flex';
    }
    
    /**
     * Close world map view
     */
    closeWorldMap() {
        if (this.worldMapModal) {
            this.worldMapModal.style.display = 'none';
        }
    }
    
    /**
     * Disable game mode
     */
    disableGameMode() {
        // Stop game loop
        if (this.gameLoop) {
            this.gameLoop.stop();
        }
        
        // Hide game UI
        if (this.gameUIContainer) {
            this.gameUIContainer.style.display = 'none';
        }
        
        // Move canvas back to planner
        if (this.gridCanvas && this.gridCanvas.canvas) {
            const plannerCanvasContainer = document.querySelector('.canvas-layers');
            if (plannerCanvasContainer) {
                plannerCanvasContainer.appendChild(this.gridCanvas.canvas);
            }
        }
        
        // Show planner UI
        const plannerContainer = document.querySelector('.city-planner');
        if (plannerContainer) {
            plannerContainer.style.display = 'block';
        }
        
        // Cleanup game UI
        if (this.gameUI) {
            this.gameUI.destroy();
            this.gameUI = null;
        }
        
        // Clean up world map
        if (this.worldMapView) {
            this.worldMapView.destroy();
            this.worldMapView = null;
        }
        
        if (this.worldMapModal) {
            if (this.worldMapEscHandler) {
                document.removeEventListener('keydown', this.worldMapEscHandler);
            }
            this.worldMapModal.remove();
            this.worldMapModal = null;
            this.worldMapEscHandler = null;
        }
        
        // Update UI to show planner resources
        this.updateUI();
    }
    
    /**
     * Get building name for notifications
     * @param {number} buildingType - Building constant
     * @returns {string} Building name
     */
    getBuildingNameForNotification(buildingType) {
        // Simple mapping for notifications
        const names = {
            [CASA]: 'Casa',
            [CELLARIUM]: 'Cellarium',
            [CACHE]: 'Cache',
            [CUTTER]: 'Forester\'s Hut',
            [QUARRY]: 'Stone Mine',
            [MINE]: 'Iron Mine',
            [FARM]: 'Farm Estate',
            [MANOR]: 'Manor',
            [BAZAAR]: 'Bazaar',
            [WHARF]: 'Wharf'
        };
        return names[buildingType] || 'Building';
    }
}

